import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { naytaPvm } from "./pvm";
import { useTaulukko } from "./taulukko";
import { nayttonimi, nayttonimiPieni, selite } from "./nayttonimi";
import { luku } from "./luvut";
import { KOHDE_TYOLAJI, type KohdeTyolaji,
  type Asiakas,
  type AsiakasRooli,
  type Asiakasrooli,
  type KohteenAsiakas,
  api, ApiError, maanantai, pvmTeksti, lisaaPaivia,
  KOHDE_TILA, varaaKapasiteettia,
  type Kayttaja, type Kohde, type KohdeTila, type Osaamisluokka, type Resurssitarve,
  type Patevyystyyppi, type KohdePatevyysvaatimus, type PerehdytysVaatimus, Perehdytystilanne,
} from "./api";import { Tilasana } from "./Tila";
import { Estelaatikko } from "./Este";


/** Saatavuuden ihmisluettava teksti. */
import type { KohdePaallekkaisyys } from "./shared/types";

import { Ohje } from "./Ohje";
import { useKohde } from "./Kohdekonteksti";
import { t as tr, virheTeksti } from "./kieli";
/* ═══════════════════════════════════════════════════════════
   Työkohteet ja resurssitarpeet.

   Resurssitarve on se syöte, jonka varassa vajelaskenta toimii:
   ilman sitä järjestelmä tietää kuka on varattu, muttei sitä
   riittääkö porukka. Siksi tarpeet ovat samassa näkymässä eivätkä
   piilossa asetuksissa.
   ═══════════════════════════════════════════════════════════ */

export default function Kohteet({ kayttaja, osoiteHaku, asetaOsoiteHaku }: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r));
  /* Sidonta ja purku OMALLA ehdollaan (Toni 23.9.): työnjohtaja tietää kuka on
     kohteessa, ja palvelin sallii hänelle sidonnan (`POST /api/tarpeet/:id/sidonta`,
     `DELETE /api/sidonta/:id`). Tarpeen ja vaatimusten muokkaus pysyy
     `saaMuokata`lla. Sama roolilista kuin reitissä — kaksi paikkaa, yksi sääntö. */
  const saaSitoa = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  /* Tilaaja näkee työn, ei työvoimaa (Toni 26.9.2026). Sama ehto kuin API:n `onAsiakastunnus`: tarpeet ovat
     tilaajalle kiellettyjä (403), ja pätevyysvaatimuksista tulee vain vaatimus ilman henkilörivejä. */
  const tilaaja = kayttaja.laji === "ulkopuolinen";
  /* 178 (Toni 26.9.2026): perehdytykset tilaajalle vain kohteen lukuoikeudella — sama ehto kuin API
     (onAsiakastunnus + onOikeus('perehdytykset_luku', kohde)). Osiota ei piirretä ilman, eikä 403 näy virheenä. */
  const perehdytysNakyy = (kohdeId: number) => !tilaaja || kayttaja.oikeudet.includes("perehdytykset_luku")
    || (kayttaja.kohdeOikeudet?.["perehdytykset_luku"] ?? []).includes(kohdeId);

  /* 278 (Toni 3.11.2026 klo 15.10 kohta 4): vain pääkäyttäjä arkistoi ja palauttaa — sama ehto kuin reitissä. */
  const saaArkistoida = kayttaja.roolit.includes("paakayttaja");
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  /* Arkistoidut piilossa oletuksena; «Näytä arkistoidut» tuo ne listaan merkinnällä. Lista ei koske yläpalkin valitsinta. */
  const [naytaArkistoidut, setNaytaArkistoidut] = useState(false);
  const arkistoituja = useMemo(() => kohteet.filter((k) => k.arkistoitu).length, [kohteet]);
  const nakyvat = useMemo(() => naytaArkistoidut ? kohteet : kohteet.filter((k) => !k.arkistoitu), [kohteet, naytaArkistoidut]);
  // A3–A7: jaettu lajittelu ja haku (taulukko.tsx).
  const t = useTaulukko(nakyvat, {
    // Toni 10.9.: kysymys jolla Työkohteet avataan on «mitkä ovat käynnissä», ei «mikä alkoi
    // viimeksi». Tila ennen päivää: käynnissä ensin, niiden sisällä alkamispäivä uusin ensin,
    // päättyneet lopuksi.
    avaimet: { tunnus: (k) => k.tunnus, nimi: (k) => k.nimi, asiakas: (k) => k.asiakas, laji: (k) => k.laji, alkaa: (k) => k.alkaa, paattyy: (k) => k.paattyy,
               tila: (k) => ({ aktiivinen: 0, suunnitteilla: 1, keskeytetty: 2, peruuntunut: 3, valmis: 4 } as Record<string, number>)[k.tila] ?? 5 },
    hakukentat: (k) => [k.tunnus, k.nimi, k.asiakas, k.tila],
    oletus: "tila", toissijainen: { avain: "alkaa", suunta: -1 }, yksikko: tr("koh.yksikko"), osoiteHaku, asetaOsoiteHaku,
  });
  const [luokat, setLuokat] = useState<Osaamisluokka[]>([]);
  const [avattu, setAvattu] = useState<number | null>(null);
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lisataan, setLisataan] = useState(false);

  const lataa = useCallback(async () => {
    setLadataan(true);
    setVirhe(null);
    try {
      /* Tilaajalle osaamisluokat on kielletty (API `onAsiakastunnus` → 403 ei_tilaajalle), ja
         Promise.all kaatoi koko näkymän: mitattu 26.9.2026, tilaaja ei nähnyt edes omaa kohdettaan.
         Luokkia tarvitaan vain tarpeen lisäyslomakkeessa. Reittiä ei avata — se on 15 estetyn joukossa. */
      const [k, o] = await Promise.all([api.kohteet(),
        kayttaja.laji === "ulkopuolinen" ? Promise.resolve([] as Osaamisluokka[]) : api.osaamisluokat()]);
      setKohteet(k);
      setLuokat(o);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.haku"));
    } finally {
      setLadataan(false);
    }
  }, [kayttaja.laji]);

  useEffect(() => { void lataa(); }, [lataa]);
  /* Testikortti 3221282 kohdat 2 ja 4 (Toni 4.11.2026 klo 11.55): arkistoinnin, palautuksen tai uuden kohteen jälkeen myös
     yläpalkin valitsin luetaan uudelleen. Ennen tätä valitsin näytti vanhan joukon F5:een asti. */
  const { paivita: paivitaValitsin } = useKohde();
  const muuttui = useCallback(async () => { await lataa(); await paivitaValitsin(); }, [lataa, paivitaValitsin]);

  if (virhe) {
    return (
      <div className="tyhja">
        <p>{virhe}</p>
        <button className="nappi nappi--paa" onClick={() => void lataa()}>{tr("koh.yrita_uudelleen")}</button>
      </div>
    );
  }

  return (
    <>
      <header className="head">
        <div className="eyebrow">{tr("valikko.tyokohteet")}</div>
        <div className="head__rivi">
          <h1>{tr("koh.otsikko")}</h1>
          {saaMuokata && !lisataan && (
            <button className="nappi nappi--paa" onClick={() => setLisataan(true)}>
              {tr("koh.lisaa_kohde")}
            </button>
          )}
        </div>
      </header>

      {/* Ohje ylös (Toni 23.9.2026): kuka saa tehdä mitä, ja mitä luvut tarkoittavat. */}
      <Ohje
        kuka={<>{tr("koh.ohje.kuka")}</>}
        luvut={<>{tr("koh.ohje.luvut")}</>} />

      {lisataan && (
        <UusiKohde
          peruuta={() => setLisataan(false)}
          valmis={async () => { setLisataan(false); await muuttui(); }}
        />
      )}

      {ladataan && <div className="lataa lataa--ohut">{tr("ltar.ladataan")}</div>}

      {!ladataan && kohteet.length === 0 && !lisataan && (
        <div className="tyhja">
          <h2>{tr("koh.ei_kohteita")}</h2>
          <p>{tr("koh.ei_kohteita_ohje")}</p>
        </div>
      )}

      {kohteet.length > 0 && <Aikajana versio={kohteet.length} />}

      {kohteet.length > 0 && (
        <section className="panel scroll">
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
            {t.Haku({ placeholder: tr("koh.haku") })}
            {t.piilotettu > 0 && <span className="dim">{tr("asi.piilossa", { n: t.piilotettu })}</span>}
            <label className="dim" data-nayta-arkistoidut={naytaArkistoidut ? "1" : "0"} data-arkistoituja={arkistoituja}>
              <input type="checkbox" checked={naytaArkistoidut} onChange={(e) => setNaytaArkistoidut(e.target.checked)} />{" "}
              {tr("koh.nayta_arkistoidut", { n: arkistoituja })}
            </label>
          </div>
          <table className="tbl tbl--kohteet">
            <thead>
              <tr>
                <th className="th--nimi">{t.Otsikko("tunnus", tr("koh.tunnus"))}</th>
                <th className="th--vasen">{t.Otsikko("nimi", tr("koh.nimi"))}</th>
                <th className="th--vasen">{t.Otsikko("asiakas", tr("koh.asiakas"))}</th>
                <th>{t.Otsikko("laji", tr("koh.laji"))}</th>
                <th>{t.Otsikko("alkaa", tr("mit.alkaa"))}</th>
                <th>{t.Otsikko("paattyy", tr("mit.paattyy"))}</th>
                <th>{t.Otsikko("tila", tr("nippu.tila"))}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {t.rivit.map((k) => (
                <KohdeRivi
                  key={k.id} kohde={k} luokat={luokat} saaMuokata={saaMuokata} saaSitoa={saaSitoa} saaArkistoida={saaArkistoida} tilaaja={tilaaja}
                  perehdytykset={perehdytysNakyy(k.id)}
                  paivita={muuttui}
                  auki={avattu === k.id}
                  avaa={() => setAvattu(avattu === k.id ? null : k.id)}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}

    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Aikajana.

   Rivit ovat kohteita, eivät henkilöitä — se vastaa kysymykseen
   "mitä on käynnissä yhtä aikaa", jota henkilöruudukosta ei näe.
   Alin rivi laskee montako kohdetta kullakin viikolla on käynnissä:
   siinä päällekkäisyys näkyy lukuna eikä pelkkänä palkkirykelmänä.
   ═══════════════════════════════════════════════════════════ */

const AIKAJANA_VIIKKOJA = 12;

/** versio muuttuu kun kohteita lisätään tai poistetaan. Ilman sitä
 *  aikajana jäisi näyttämään sitä tilaa, joka oli sivun latautuessa —
 *  lista päivittyisi ja aikajana ei, samasta datasta eri hetkistä. */
function Aikajana({ versio }: { versio: number }) {
  const [alku, setAlku] = useState(() => pvmTeksti(maanantai(new Date())));
  const loppu = useMemo(
    () => pvmTeksti(lisaaPaivia(new Date(alku), AIKAJANA_VIIKKOJA * 7 - 1)),
    [alku]
  );
  const [rivit, setRivit] = useState<KohdePaallekkaisyys[]>([]);
  const [ladataan, setLadataan] = useState(true);

  useEffect(() => {
    setLadataan(true);
    api.kohteidenAikajana(alku, loppu)
      .then(setRivit)
      .catch(() => setRivit([]))
      .finally(() => setLadataan(false));
  }, [alku, loppu, versio]);

  const viikot = useMemo(() => {
    const lista: { alku: string; nro: number }[] = [];
    for (let i = 0; i < AIKAJANA_VIIKKOJA; i++) {
      const d = lisaaPaivia(new Date(alku), i * 7);
      lista.push({ alku: pvmTeksti(d), nro: viikkoNro(d) });
    }
    return lista;
  }, [alku]);

  /** Kuinka monta kohdetta on käynnissä kullakin viikolla.
   *
   * ⛔ TILASUODATIN, JA SE PUUTTUI. Rivi laski jokaisen kohteen jonka
   * päivämäärät osuvat viikkoon — myös holdatun ja suunnitteilla
   * olevan. Demossa se näytti "9 käynnissä yhtä aikaa" kun käynnissä
   * oli kuusi, yksi oli holdissa ja kaksi suunnitteilla.
   *
   * Ja näkymä oli ristiriidassa itsensä kanssa: rivi yllä MERKITSEE
   * kohteen joka ei varaa kapasiteettia, ja sen oma kommentti sanoo
   * "kohde joka ei varaa ei saa kadota — eikä näyttää käynnissä
   * olevalta". Yhteenvetorivi teki juuri jälkimmäisen.
   *
   * Sama predikaatti kuin merkinnässä (`varaaKapasiteettia`), ei uusi
   * ehto: kaksi lukijaa samasta säännöstä, ei kahta sääntöä.
   *
   * Ei varaavat lasketaan erikseen eikä pudoteta — poistettu rivi on
   * väite, ei tyhjyys. */
  const osuuViikkoon = (r: { alkaa: string | null; paattyy: string | null }, v: { alku: string }) => {
    const vkLoppu = pvmTeksti(lisaaPaivia(new Date(v.alku), 6));
    return (r.alkaa ?? "0000-01-01") <= vkLoppu && (r.paattyy ?? "9999-12-31") >= v.alku;
  };
  const kaynnissa = useMemo(
    () => viikot.map((v) => rivit.filter((r) =>
      osuuViikkoon(r, v) && varaaKapasiteettia(r.tila as KohdeTila)).length),
    [viikot, rivit]
  );
  const eiVaraa = useMemo(
    () => viikot.map((v) => rivit.filter((r) =>
      osuuViikkoon(r, v) && !varaaKapasiteettia(r.tila as KohdeTila)).length),
    [viikot, rivit]
  );

  const siirra = (paivia: number) => setAlku(pvmTeksti(lisaaPaivia(new Date(alku), paivia)));

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{tr("koh.aikajana")}</h2>
        <div className="head__ohjaus">
          <button className="nappi nappi--kevyt" onClick={() => siirra(-28)}>←</button>
          <button className="nappi nappi--kevyt" onClick={() => setAlku(pvmTeksti(maanantai(new Date())))}>{tr("koh.tanaan")}</button>
          <button className="nappi nappi--kevyt" onClick={() => siirra(28)}>→</button>
        </div>
      </div>

      {ladataan && <div className="lataa lataa--ohut">{tr("ltar.ladataan")}</div>}

      <div className="scroll">
        <table className="tbl tbl--aikajana">
          <thead>
            <tr>
              <th className="th--nimi">{tr("koh.tyomaa")}</th>
              {viikot.map((v) => <th key={v.alku}>{v.nro}</th>)}
            </tr>
          </thead>
          <tbody>
            {rivit.map((r) => (
              <tr key={r.kohde_id}
                    className={varaaKapasiteettia(r.tila as KohdeTila) ? "" : "tr--ei-varaa"}>
                <th className="th--nimi">
                  <span className="lyh">{r.tunnus}</span>
                  <span className="kokonimi">{r.nimi}</span>
                    {/* Aikajana on se nakyma josta katsotaan mika on
                        kaynnissa milloinkin. Kohde joka ei varaa ei saa
                        kadota - eika nayttaa kaynnissa olevalta. */}
                    {!varaaKapasiteettia(r.tila as KohdeTila) && (
                      <span className="merkki merkki--ei-varaa"
                            title={selite("kohde_tila", r.tila as KohdeTila)}>
                        {nayttonimi("kohde_tila", r.tila as KohdeTila)}
                      </span>
                    )}
                </th>
                {viikot.map((v) => {
                  const vkLoppu = pvmTeksti(lisaaPaivia(new Date(v.alku), 6));
                  const kesken = (r.alkaa ?? "0000-01-01") <= vkLoppu &&
                                 (r.paattyy ?? "9999-12-31") >= v.alku;
                  const alkaaTassa = !!r.alkaa && r.alkaa >= v.alku && r.alkaa <= vkLoppu;
                  const paattyyTassa = !!r.paattyy && r.paattyy >= v.alku && r.paattyy <= vkLoppu;
                  return (
                    <td key={v.alku} className="td--jana">
                      {kesken && (
                        <span
                          className={"jana"
                            + (alkaaTassa ? " jana--alku" : "")
                            + (paattyyTassa ? " jana--loppu" : "")
                            + (r.laji === "jatkuva" ? " jana--jatkuva" : "")}
                          title={tr("koh.jana_title", { tunnus: r.tunnus, n: r.henkiloita, h: luku(r.tunteja) })}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}

            <tr className="tr--summa">
              <th className="th--nimi">{tr("koh.kaynnissa_yhta_aikaa")}</th>
              {kaynnissa.map((n, i) => (
                <td key={viikot[i]!.alku} className={"td--summa" + (n > 2 ? " td--summa-korkea" : "")}>
                  {n > 0 ? n : ""}
                  {/* Ei varaavat näkyvät erikseen eivätkä katoa: jos
                      luku putoaa 9:stä 6:een ilman selitystä, lukija
                      etsii kadonneita kolmea. `+3` kertoo missä ne
                      ovat — ja että ne eivät kuormita. */}
                  {eiVaraa[i]! > 0 && (
                    <span className="dim" style={{ fontWeight: 400, marginLeft: 3 }}
                          title={tr("koh.ei_varaa_title")}>
                      +{eiVaraa[i]}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {rivit.length === 0 && !ladataan && (
        <p className="dim laajennus">{tr("koh.ei_tyomaita_valilla")}</p>
      )}
    </section>
  );
}

/* ── yksi kohde ja sen tarpeet ──────────────────────────── */

/**
 * Milloin pätevyyden on oltava kunnossa: jakson alkupäivä.
 *
 * Eräpäivä ei ole oma kenttänsä vaan `alkaa` — sama käsite yhdessä paikassa.
 * Etäisyys tulee palvelimelta (`paivia_alkuun`), koska «tänään» selaimen
 * kellosta olisi eri luku kuin se jonka kaikki muut näkevät.
 *
 * ⛔ Kolmatta tilaa ei ole. «Ei tiedetä milloin» vaatisi tarpeen jonka
 * alkupäivä puuttuu, ja sellaista ei voi syntyä: `jakso` on NOT NULL ja
 * luontireitti vaatii `alkaa`-kentän. Mitattu 20.9.2026 — avoin alaraja
 * 0 riviä demossa, protot'ssa, pekilossa ja aaltoflow'ssa. Tyhjä muoto
 * tässä on siis vika eikä tila, ja se sanotaan ääneen.
 */
function erapaiva(t: Resurssitarve): string {
  if (t.paivia_alkuun == null) return tr("koh.era.ei_saatu");
  const n = t.paivia_alkuun;
  // Oma rivinsä tarjokkaan alla (Toni 23.9.), joten iso alkukirjain ja pilkku.
  if (n < 0)  return tr("koh.era.myohassa", { n: -n, pvm: naytaPvm(t.alkaa) ?? "" });
  if (n === 0) return tr("koh.era.tanaan", { pvm: naytaPvm(t.alkaa) ?? "" });
  return tr("koh.era.aikaa", { pvm: naytaPvm(t.alkaa) ?? "", n });
}

/** ISO-päivä + n päivää. UTC, jotta kesäaika ei siirrä päivää. */
function isoPlus(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * «Vapaa X alkaen» tarjokkaalle, jonka sidonnat peittävät tarpeen jakson
 * ALUN (Markku 21.9.). Palauttaa ensimmäisen vapaan päivän, jos se on
 * jakson alun jälkeen; null = vapaa heti alusta; "ei" = ei vapaata päivää
 * tällä jaksolla (sidottu toistaiseksi tai loppuun asti).
 */
function vapaaAlkaen(
  t: Resurssitarve, sidottu: { alkaa: string; paattyy: string | null }[],
): string | null | "ei" {
  let d = t.alkaa;
  for (const s of [...sidottu].sort((a, b) => a.alkaa.localeCompare(b.alkaa))) {
    if (s.alkaa > d) break;
    if (s.paattyy === null) return "ei";
    if (s.paattyy >= d) d = isoPlus(s.paattyy, 1);
  }
  if (t.paattyy && d > t.paattyy) return "ei";
  return d === t.alkaa ? null : d;
}

/** Tarjokkaan tila sanana (Toni 23.9.: «Ei täytä» → «Ei täytä vaatimuksia»). */
const TARJOKAS_TILA: Record<string, { sana: () => string; laji: "kunnossa" | "varoitus" | "vika" | "ei_mitattu" }> = {
  tayttaa:       { sana: () => tr("koh.tarjokas.tayttaa"), laji: "kunnossa" },
  uusittava:     { sana: () => tr("koh.tarjokas.uusittava"), laji: "varoitus" },
  ei_kelpaa_nyt: { sana: () => tr("koh.tarjokas.ei_kelpaa_nyt"), laji: "vika" },
  ei_tayta:      { sana: () => tr("koh.tarjokas.ei_tayta"), laji: "ei_mitattu" },
};

function KohdeRivi({
  kohde, luokat, saaMuokata, saaSitoa, saaArkistoida, tilaaja, perehdytykset, paivita, auki, avaa,
}: {
  kohde: Kohde; luokat: Osaamisluokka[]; saaMuokata: boolean; saaSitoa: boolean; saaArkistoida: boolean; tilaaja: boolean; perehdytykset: boolean;
  paivita: () => Promise<void>;
  auki: boolean; avaa: () => void;
}) {
  const [tarpeet, setTarpeet] = useState<Resurssitarve[] | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  const [luokkaId, setLuokkaId] = useState<number | "">("");
  const [alkaa, setAlkaa] = useState(kohde.alkaa ?? "");
  // Jos kohteella ei ole loppua, tarpeellakaan ei ole oletusta.
  const [paattyy, setPaattyy] = useState(kohde.paattyy ?? "");
  const [maara, setMaara] = useState("1");

  // Pätevyysvaatimukset: mitä työmaalla tarvitaan, keillä on, kuka vapaana.
  const [vaatimukset, setVaatimukset] = useState<KohdePatevyysvaatimus[] | null>(null);
  const [ptyypit, setPtyypit] = useState<Patevyystyyppi[]>([]);

  // Vaihe 1: «N ×» poistui vaatimukselta. Maaran kertoo TARVE, ja «vain
  // toisella heista» ilmaistaan jakamalla tarverivi — kaksi lukua samasta
  // asiasta eri riveilla on kahden totuuden alku.

  /** 145: kenen sidonta odottaa perustetta — avain «tarve:henkilo». */
  const [perusteRivi, setPerusteRivi] = useState<string | null>(null);
  const [peruste, setPeruste] = useState("");

  /** Minkä tarverivin tarjokaslista on auki. Sidonta tehdään siitä. */
  const [auki2, setAuki2] = useState<number | null>(null);
  /** Vaatimukset jotka syntyvät TARPEEN mukana: yksi teko, ei kahta. */
  const [uudetVaatimukset, setUudetVaatimukset] =
    useState<{ tyyppi: number | ""; luku: string }[]>([]);
  /** Minkä rivin «+ vaatimus» on auki, ja sen syöte. */
  const [lisaysRivi, setLisaysRivi] = useState<number | null>(null);
  const [lisaysTyyppi, setLisaysTyyppi] = useState<number | "">("");
  /** Tyhjä = kaikilta. Ei alkuarvoa «1» — se oli oletus joka näytti valinnalta. */
  const [lisaysLuku, setLisaysLuku] = useState("");
  const [tuntia, setTuntia] = useState("8");
  /** Kenen sidontalomake on auki — avain «tarve:henkilo». */
  const [sidontaRivi, setSidontaRivi] = useState<string | null>(null);
  /** Sidonnan laajuus: koko tarpeen jakso vai valittu väli (Toni 23.9.). */
  const [laajuus, setLaajuus] = useState<"koko" | "osa">("koko");
  const [sAlkaa, setSAlkaa] = useState("");
  const [sPaattyy, setSPaattyy] = useState("");
  /** Tyyppi JOHDETAAN rajapinnasta: käsin kirjoitettu kopio oli jo erkaantunut
   *  (siitä puuttuivat tarverivit, jotka reitti palauttaa). */
  const [kohdistamattomat, setKohdistamattomat] =
    useState<Awaited<ReturnType<typeof api.kohdistamattomatVaatimukset>>>([]);

  const haeTarpeet = useCallback(async () => {
    try { setTarpeet(await api.tarpeet(kohde.id)); }
    catch { setTarpeet([]); }
  }, [kohde.id]);

  const haeVaatimukset = useCallback(async () => {
    try {
      setVaatimukset(await api.patevyysvaatimukset(kohde.id));
      // Kohdistus tarveriveihin on muokkaajan työtä ja kantaa tarverivien rakenteen — ei tilaajalle.
      if (!tilaaja) setKohdistamattomat(await api.kohdistamattomatVaatimukset(kohde.id));
    }
    catch { setVaatimukset([]); }
  }, [kohde.id, tilaaja]);

  // Tilaajalle ei haeta: reitti on 403, ja haeTarpeet muuttaisi virheen tyhjäksi listaksi («ei tarpeita» = väite).
  useEffect(() => { if (auki && !tilaaja && tarpeet === null) void haeTarpeet(); }, [auki, tilaaja, tarpeet, haeTarpeet]);

  useEffect(() => {
    if (!auki || vaatimukset !== null) return;
    void haeVaatimukset();
    if (ptyypit.length === 0) api.patevyystyypit().then(setPtyypit).catch(() => {});
  }, [auki, vaatimukset, haeVaatimukset, ptyypit.length]);

  // Litteä (vaatimus × osaaja) ryhmitellään pätevyyksittäin näkymää varten.
  const ryhmat = useMemo(() => {
    // Kohdistetut vaatimukset renderöidään TARPEEN alla. Jos ne näkyisivät myös
    // tässä listassa, sama vaatimus olisi ruudulla kahdesti ja toinen niistä
    // ilman tietoa siitä ketä se koskee — eli juuri se tila josta vaihe 1 pyrki
    // pois. Litteä lista on siksi kohdistamattomien lista.
    const kohdistetut = new Set(
      (tarpeet ?? []).flatMap((x) => (x.vaatimukset ?? []).map((v) => v.patevyystyyppi_id)));
    const m = new Map<number, { patevyys: string; lukumaara: number; osaajat: KohdePatevyysvaatimus[] }>();
    for (const r of vaatimukset ?? []) {
      if (kohdistetut.has(r.patevyystyyppi_id)) continue;
      let g = m.get(r.patevyystyyppi_id);
      if (!g) { g = { patevyys: r.patevyys, lukumaara: r.lukumaara, osaajat: [] }; m.set(r.patevyystyyppi_id, g); }
      if (r.henkilo_id) g.osaajat.push(r);
    }
    return [...m.entries()].map(([id, g]) => ({ id, ...g }));
  }, [vaatimukset, tarpeet]);

  /** Tilanvaihto on yksi kutsu ja koko listan lataus, koska muutos
   *  vaikuttaa muidenkin kohteiden vajelukuihin — ei vain tämän rivin. */
  async function vaihdaLuovutus(pvm: string | null) {
    if (tallennetaan) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.muokkaaKohdetta(kohde.id, { luovutettu_pvm: pvm });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.luovutus"));
    } finally { setTallennetaan(false); }
  }

  async function vaihdaTyolaji(uusi: KohdeTyolaji) {
    if (uusi === (kohde.tyolaji ?? "muu") || tallennetaan) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.muokkaaKohdetta(kohde.id, { tyolaji: uusi });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.tyolaji"));
    } finally { setTallennetaan(false); }
  }

  /* 209 (vaihe c): raporttien oletuskieli. Tulostaja voi valita toisen jokaisessa tulosteessa (Toni 8.10.2026). */
  async function vaihdaDokumentaatiokieli(uusi: string) {
    if (uusi === (kohde.dokumentaatiokieli ?? "") || tallennetaan) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.muokkaaKohdetta(kohde.id, { dokumentaatiokieli: uusi || null });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.kieli"));
    } finally { setTallennetaan(false); }
  }

  /* 221 (T464) ja 222 (T466): kohteen kaksi kyllä/ei-asetusta samalla tallennuksella. */
  async function vaihdaAsetus(kentta: "nayta_lahtoaukot_tilaajalle" | "linjatarkastus_edustaja_pakollinen", arvo: boolean) {
    if (tallennetaan) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.muokkaaKohdetta(kohde.id, { [kentta]: arvo });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.asetus"));
    } finally { setTallennetaan(false); }
  }

  async function vaihdaTila(uusi: KohdeTila) {
    if (uusi === kohde.tila || tallennetaan) return;
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.muokkaaKohdetta(kohde.id, { tila: uusi });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.tila"));
    } finally { setTallennetaan(false); }
  }

  async function lisaa() {
    if (luokkaId === "") return;
    const m = Number(maara.replace(",", "."));
    if (!(m > 0)) { setIlmoitus(tr("koh.ilm.maara_nolla")); return; }
    // ⛔ Murtolukua ei PYÖRISTETÄ vaan estä, ja syy sanotaan. Hiljainen
    // pyöristys näyttäisi onnistuneelta tallennukselta, ja ihminen jäisi
    // luulemaan että hänen lukunsa kelpasi.
    if (!Number.isInteger(m)) {
      setIlmoitus(tr("koh.ilm.kokonaisluku"));
      return;
    }
    if (!alkaa) { setIlmoitus(tr("koh.ilm.anna_alku")); return; }
    if (paattyy && paattyy < alkaa) { setIlmoitus(tr("mit.virhe.loppu_ennen")); return; }
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      // Vaatimuksen luku tarkistetaan ENNEN tallennusta: «2 × Hitsaaja mutta
      // 3 × Työturvallisuuskortti» on se virhe jonka Toni näki ruudulla, ja
      // syöttöhetki on ainoa paikka jossa sen voi estää ennen syntymistä.
      const vaaditut = uudetVaatimukset
        .filter((v) => v.tyyppi !== "")
        .map((v) => ({ tyyppi: v.tyyppi as number, luku: Number(v.luku.replace(",", ".")) || 1 }));
      const liikaa = vaaditut.find((v) => v.luku > m);
      if (liikaa) {
        setIlmoitus(tr("koh.ilm.vaatimus_yli", { n: liikaa.luku, m }));
        setTallennetaan(false);
        return;
      }
      const { id: tarveId } = await api.lisaaTarve(kohde.id, {
        osaamisluokka_id: luokkaId, alkaa, maara: m,
        // Kenttä jätetään kokonaan pois kun loppua ei ole: puuttuva
        // loppupäivä tarkoittaa toistaiseksi. Jatkuvalla sopimuksella
        // ei ole loppua, eikä tarpeellakaan silloin.
        ...(paattyy && { paattyy }),
      });
      for (const v of vaaditut) {
        await api.lisaaPatevyysvaatimus(kohde.id,
          { patevyystyyppi_id: v.tyyppi, lukumaara: v.luku, resurssitarve_id: tarveId });
      }
      setUudetVaatimukset([]);
      await haeTarpeet();
      setIlmoitus(vaaditut.length > 0
        ? tr("koh.ilm.tarve_ja_n", { n: vaaditut.length })
        : tr("koh.ilm.tarve_lisatty"));
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  /**
   * Koko tarpeen poisto. Vahvistus, koska nappi on samassa sarakkeessa kuin
   * vaatimusrivin «Poista» ja teot ovat eri kokoluokkaa: toinen poistaa yhden
   * vaatimuksen, toinen koko miehitystarpeen vaatimuksineen.
   */
  async function poistaKokoTarve(t2: Resurssitarve) {
    const v = t2.vaatimukset?.length ?? 0;
    if (!window.confirm(
      tr("koh.vahv.poista_tarve", { n: t2.maara, luokka: t2.osaamisluokka }) +
      (v > 0 ? "\n" + tr("koh.vahv.poista_tarve_vaatimukset", { n: v }) : "")
    )) return;
    await poista(t2.id);
  }

  async function poista(id: number) {
    setTallennetaan(true);
    try {
      await api.poistaTarve(id);
      await haeTarpeet();
      setIlmoitus(tr("koh.ilm.poistettu"));
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("liite.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  /**
   * Sidonta suoraan tarveriviltä (Toni 19.9., lomake 23.9.).
   *
   * ⛔ Ennen 23.9. tämä kutsui allokaatioreittiä: työpäivät syntyivät, mutta
   * 159:n sidontataulu ei saanut riviä — «sidottu X / N» ei liikkunut eikä
   * tarjokas pudonnut listalta. Nyt sidonta tehdään tarpeeseen, ja palvelin
   * kirjoittaa samassa transaktiossa työpäivät sidotulle välille.
   *
   * Laajuus: koko jakso (alku = tarpeen alku, loppu tyhjä = seuraa tarvetta)
   * tai valittu väli. Loppupäivä on mukaan lukien; väli tarpeen ulkopuolella
   * torjutaan palvelimella nimetysti.
   */
  async function sido(t2: Resurssitarve, henkiloId: number, nimi: string) {
    const h = Number(tuntia.replace(",", "."));
    if (!(h > 0 && h <= 24)) { setIlmoitus(tr("koh.ilm.tyopaiva_pituus")); return; }
    const osa = laajuus === "osa";
    if (osa && !sAlkaa) { setIlmoitus(tr("koh.ilm.anna_valin_alku")); return; }
    if (osa && sPaattyy && sPaattyy < sAlkaa) { setIlmoitus(tr("koh.ilm.valin_loppu")); return; }
    setTallennetaan(true); setIlmoitus(null);
    try {
      const v = await api.sidoTarpeeseen(t2.id, {
        resurssi_id: henkiloId, tunnit_per_paiva: h,
        ...(osa ? { alkaa: sAlkaa, ...(sPaattyy ? { paattyy: sPaattyy } : {}) } : {}),
        ...(peruste.trim() ? { peruste: peruste.trim() } : {}),
      });
      await haeTarpeet();
      setSidontaRivi(null); setPerusteRivi(null); setPeruste("");
      // Varoitukset EIVÄT estä (ylitys, poissaolo), mutta ne sanotaan — sidonta
      // joka vaikenee ylityksestä on sama kuin luku ilman yksikköä.
      setIlmoitus(tr("koh.ilm.sidottu", { nimi }) + " "
        + (v.toistaiseksi
            ? tr("koh.ilm.toistaiseksi_ei_paivia")
            : tr("koh.ilm.paivia_kirjattu", { n: v.paivia }))
        + (v.varoitukset > 0 ? " " + tr("koh.ilm.varoitukset", { n: v.varoitukset }) : ""));
    } catch (e) {
      // 145: puuttuva peruste ei ole virhe vaan kysymys. Palvelin nimeaa
      // MITKA patevyydet puuttuvat, ja kentta avautuu siihen vastaamaan.
      if (e instanceof ApiError && e.virhe === "peruste_puuttuu") {
        setPerusteRivi(`${t2.id}:${henkiloId}`);
      }
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.sidonta"));
    } finally { setTallennetaan(false); }
  }

  /** Avaa sidontalomakkeen: oletus koko jakso, väli esitäytetty tarpeen jaksolla. */
  function avaaSidonta(t2: Resurssitarve, henkiloId: number) {
    const avain = `${t2.id}:${henkiloId}`;
    if (sidontaRivi === avain) { setSidontaRivi(null); return; }
    setSidontaRivi(avain); setPerusteRivi(null); setPeruste("");
    setLaajuus("koko"); setSAlkaa(t2.alkaa); setSPaattyy(t2.paattyy ?? "");
  }

  /**
   * Purku. Sidontalomake kirjoitti myös työpäivät, joten purku poistaa ne
   * samalta väliltä — muuten ne jäisivät «kohteessa, ei tarpeeseen
   * sidottu» -riviksi, jota kukaan ei tarkoittanut.
   */
  /* Päätös 13 (Toni 30.9.2026): vanha allokaatio sidotaan tehtävään samalta riviltä — vain tarve vaihtuu.
     Peruste kysytään henkilöittäin (400 peruste_puuttuu avaa kentän), ja se tallentuu sidonnan_peruste-riviksi. */
  const [liitaPerusteRivi, setLiitaPerusteRivi] = useState<string | null>(null);
  const [liitaPeruste, setLiitaPeruste] = useState("");
  /** Palvelimen nimeämä puute (400 peruste_puuttuu -viesti) laatikon riville. */
  const [liitaPuuttuu, setLiitaPuuttuu] = useState<string | null>(null);
  async function liitaAllokaatiot(t2: Resurssitarve, henkiloId: number, nimi: string) {
    setTallennetaan(true); setIlmoitus(null);
    try {
      const v = await api.liitaAllokaatiot(t2.id, { resurssi_id: henkiloId, ...(liitaPeruste.trim() ? { peruste: liitaPeruste.trim() } : {}) });
      await haeTarpeet();
      setLiitaPerusteRivi(null); setLiitaPeruste(""); setLiitaPuuttuu(null);
      setIlmoitus(tr(v.peruste_kirjattu ? "koh.ilm.liitetty_peruste" : "koh.ilm.liitetty", { nimi, n: v.liitetty }));
    } catch (e) {
      if (e instanceof ApiError && e.virhe === "peruste_puuttuu") { setLiitaPerusteRivi(`${t2.id}:${henkiloId}`); setLiitaPuuttuu(e.message.replace(/^[^:]*:\s*/, "")); }
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.sitominen"));
    } finally { setTallennetaan(false); }
  }

  async function puraSidonta(s: NonNullable<Resurssitarve["sidonnat"]>[number]) {
    const vali = `${naytaPvm(s.alkaa)}–${s.paattyy ? naytaPvm(s.paattyy) : tr("koh.toistaiseksi")}`;
    if (!window.confirm(tr("koh.vahv.pura", { nimi: s.nimi, vali }) + "\n"
      + tr("koh.vahv.pura_selite"))) return;
    setTallennetaan(true); setIlmoitus(null);
    try {
      const r = await api.puraSidonta(s.id, true);
      await haeTarpeet();
      setIlmoitus(tr("koh.ilm.purettu", { nimi: s.nimi, vali, n: r.tyopaivia_poistettu }));
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.purku"));
    } finally { setTallennetaan(false); }
  }

  /** «+ vaatimus» riviltä: vaatimus syntyy siinä missä tarve on. */
  async function lisaaRiville(t2: Resurssitarve) {
    if (lisaysTyyppi === "") return;
    // Tyhjä kenttä = KAIKILTA, ei yhdeltä. Oletus «1» asui kolmessa
    // kerroksessa (kanta, reitti, tämä lomake) ja alilaski hiljaa; nyt
    // tyhjä jätetään lähettämättä ja palvelin lukee tarpeen määrän.
    const annettu = lisaysLuku.trim();
    const n = annettu === "" ? null : Number(annettu.replace(",", "."));
    if (n !== null && (!Number.isFinite(n) || n < 1)) {
      setIlmoitus(tr("koh.ilm.lukumaara")); return;
    }
    if (n !== null && n > Number(t2.maara)) {
      setIlmoitus(tr("koh.ilm.vaatimus_yli", { n, m: t2.maara })); return;
    }
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.lisaaPatevyysvaatimus(kohde.id,
        { patevyystyyppi_id: lisaysTyyppi,
          ...(n === null ? {} : { lukumaara: n }), resurssitarve_id: t2.id });
      setLisaysTyyppi(""); setLisaysLuku(""); setLisaysRivi(null);
      await haeTarpeet();
      setIlmoitus(tr("koh.ilm.vaatimus_lisatty"));
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }


  /**
   * Kohdistetun vaatimuksen poisto tarveriviltä. Vahvistus kysytään, koska
   * poisto muuttaa tarjokasjoukkoa — ja se on juuri se luku jonka takia
   * vaatimus lisättiin. Rivi ladataan uudelleen, ettei ruutu näytä vanhaa
   * tarjokaslukua uudella vaatimuslistalla.
   */
  async function poistaRiviVaatimus(
    t: Resurssitarve, v: { id: number; nimi: string }
  ) {
    if (!window.confirm(
      tr("koh.vahv.poista_rivivaatimus", { nimi: v.nimi, n: t.maara, luokka: t.osaamisluokka }) + "\n" +
      tr("koh.vahv.tarjokkaat_muuttuu"))) return;
    setTallennetaan(true);
    try {
      await api.poistaVaatimusrivi(kohde.id, v.id);
      await haeTarpeet();
      await haeVaatimukset();
      setIlmoitus(tr("koh.ilm.rivivaatimus_poistettu", { nimi: v.nimi }));
    } catch (e2) {
      setIlmoitus(e2 instanceof ApiError ? virheTeksti(e2.virhe, e2.message) : tr("liite.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  /** 278: arkistointi ja palautus. Vahvistus kertoo mitä arkistointi tekee ja mitä ei (ei poista, ei muuta tilaa). */
  async function vaihdaArkistointi(arkistoi: boolean) {
    if (!window.confirm(tr(arkistoi ? "koh.vahv.arkistoi" : "koh.vahv.palauta", { tunnus: kohde.tunnus }))) return;
    setTallennetaan(true);
    try {
      await api.arkistoiKohde(kohde.id, arkistoi);
      await paivita();
    } catch (e) {
      window.alert(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.arkistointi"));
    } finally { setTallennetaan(false); }
  }

  /** Poisto koskee kohdistamatonta riviä; kohdistettu poistetaan tarpeen alta. */
  async function poistaVaatimus(tyyppiId: number) {
    setTallennetaan(true);
    try {
      await api.poistaPatevyysvaatimus(kohde.id, tyyppiId);
      await haeVaatimukset();
      setIlmoitus(tr("koh.ilm.vaatimus_poistettu"));
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("liite.poisto_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <>
      <tr className={[auki ? "tr--auki" : "",
                      varaaKapasiteettia(kohde.tila) ? "" : "tr--ei-varaa"]
                      .filter(Boolean).join(" ")}>
        <th className="th--nimi"><span className="lyh">{kohde.tunnus}</span>
          {kohde.entinen_tunnus && <span className="dim" data-entinen-tunnus={kohde.entinen_tunnus} title={tr("koh.tunnus_muutettu_title")}> {tr("koh.entinen", { tunnus: kohde.entinen_tunnus })}</span>}
          {kohde.jatkaa_tunnus && <span className="dim" data-jatkaa={kohde.jatkaa_tunnus}> {tr("koh.jatkaa", { tunnus: kohde.jatkaa_tunnus })}</span>}
          {kohde.arkistoitu && <span className="merkki" data-arkistoitu={kohde.id} style={{ marginLeft: 6 }}
            title={tr("koh.arkistoitu_title", { pvm: naytaPvm(kohde.arkistoitu.slice(0, 10)) ?? "" })}>{tr("koh.arkistoitu")}</span>}
        </th>
        <td className="td--vasen">{kohde.nimi}</td>
        {/* 149: rekisterin tilaaja on se joka sitoo, teksti on lähteen arvo.
            Molemmat näkyvät, koska teksti on ainoa tieto siitä mihin liitos
            perustuisi — ja «ei asetettu» on oma tilansa eikä tyhjä solu.
            Mitattu 21.9.: 12 kohdetta 13:sta on tässä tilassa. */}
        <td className="td--vasen">
          {kohde.tilaaja
            ? <>{kohde.tilaaja}
                {kohde.asiakas && kohde.asiakas !== kohde.tilaaja &&
                  <span className="dim" title={tr("koh.oma_teksti_title")}> · «{kohde.asiakas}»</span>}</>
            : <span className="dim">
                {kohde.asiakas ? <>«{kohde.asiakas}» </> : null}
                <span className="merkki" title={tr("koh.ei_liitetty_title")}>
                  {tr("koh.ei_rekisterissa")}
                </span>
                {/* 81 (Toni 5.10.2026 klo 15.33): liitos löytyi vain «Tarpeet»-napin takaa — merkinnästä pääsee nyt suoraan siihen. */}
                {saaMuokata && (
                  <button type="button" className="nappi nappi--kevyt" data-yhdista-asiakas={kohde.id} style={{ marginLeft: 6 }}
                          onClick={() => { if (!auki) avaa(); setTimeout(() => document.querySelector(`[data-kohteen-asiakkaat="${kohde.id}"]`)?.scrollIntoView({ block: "start", behavior: "smooth" }), 150); }}>
                    {tr("koh.yhdista_asiakas")}
                  </button>
                )}
              </span>}
        </td>
        <td className="mono">{kohde.laji === "jatkuva" ? tr("koh.laji.jatkuva") : tr("koh.laji.projekti")}</td>
        <td className="mono">{naytaPvm(kohde.alkaa) ?? "—"}</td>
        <td className="mono">{naytaPvm(kohde.paattyy) ?? "—"}</td>
        {/* Tila on ainoa kenttä tässä taulukossa joka muuttaa laskentaa,
            joten se on muokattavissa suoraan rivillä eikä lomakkeen takana.
            Valinnan selite on mukana vaihtoehdossa: hold ja peruuntunut
            eroavat vain siinä odotetaanko paluuta, eikä sitä eroa voi
            päätellä jälkikäteen — se on kysyttävä silloin kun ihminen
            tietää sen. */}
        <td className="td--tila">
          {saaMuokata ? (
            <select className="tilavalinta" value={kohde.tila} disabled={tallennetaan}
                    title={selite("kohde_tila", kohde.tila)}
                    onChange={(e) => void vaihdaTila(e.target.value as KohdeTila)}>
              {(Object.keys(KOHDE_TILA) as KohdeTila[]).map((t) => (
                <option key={t} value={t}>{nayttonimi("kohde_tila", t)} — {selite("kohde_tila", t)}</option>
              ))}
            </select>
          ) : (
            <span className="merkki">{nayttonimi("kohde_tila", kohde.tila)}</span>
          )}
          {/* 188 (2.10.2026): työlaji kenttänä, ei johdettuna — sama joukko kuin hinnaston mittatikut + muu. */}
          <div className="vihje" data-tyolaji={kohde.tyolaji ?? ""}>
            {saaMuokata ? (
              <label>{tr("koh.tyolaji")} <select value={kohde.tyolaji ?? "muu"} disabled={tallennetaan}
                onChange={(e) => void vaihdaTyolaji(e.target.value as KohdeTyolaji)}>
                {(Object.keys(KOHDE_TYOLAJI) as KohdeTyolaji[]).map((t) => <option key={t} value={t}>{KOHDE_TYOLAJI[t]}</option>)}
              </select></label>
            ) : (
              <>{tr("koh.tyolaji")} {KOHDE_TYOLAJI[kohde.tyolaji ?? "muu"]}</>
            )}
          </div>
          {/* 209 (vaihe c): raporttien ja tulosteiden oletuskieli — tulostaja voi valita toisen. */}
          <div className="vihje" data-dokumentaatiokieli={kohde.dokumentaatiokieli ?? ""}>
            {saaMuokata ? (
              <label>{tr("koh.dok_kieli")} <select value={kohde.dokumentaatiokieli ?? ""} disabled={tallennetaan}
                onChange={(e) => void vaihdaDokumentaatiokieli(e.target.value)}>
                <option value="">{tr("koh.org_oletus")}</option>
                <option value="fi">{tr("koh.kieli_suomi")}</option>
                <option value="en">English</option>
              </select></label>
            ) : (
              <>{tr("koh.dok_kieli")} {kohde.dokumentaatiokieli === "en" ? "English" : kohde.dokumentaatiokieli === "fi" ? tr("koh.kieli_suomi") : tr("koh.org_oletus_pieni")}</>
            )}
          </div>
          {/* 221 (T464) ja 222 (T466): tilaajalle näkyvät aukot ja linjatarkastuksen edustajavaatimus kohteittain. */}
          {(["nayta_lahtoaukot_tilaajalle", "linjatarkastus_edustaja_pakollinen"] as const).map((kentta) => (
            <div key={kentta} className="vihje" data-kohdeasetus={kentta} data-arvo={String(kohde[kentta] !== false)}>
              <label><input type="checkbox" checked={kohde[kentta] !== false} disabled={!saaMuokata || tallennetaan}
                onChange={(e) => void vaihdaAsetus(kentta, e.target.checked)} />{" "}
                {tr(kentta === "nayta_lahtoaukot_tilaajalle" ? "koh.lahtoaukot_tilaajalle" : "koh.edustaja_pakollinen")}</label>
            </div>
          ))}
          {/* 187 (päätös 22): luovutuspäivä näkyy ja on muokattavissa vain valmis-tilassa. Kanta antaa
              oletuksen tilan vaihtopäivästä ja kieltää tulevaisuuden (400 luovutettu_pvm_tulevaisuudessa). */}
          {kohde.tila === "valmis" && (
            <div className="vihje" data-luovutettu-pvm={kohde.luovutettu_pvm ?? ""}>
              {saaMuokata ? (
                <label>{tr("koh.luovutettu")} <input type="date" value={kohde.luovutettu_pvm ?? ""} disabled={tallennetaan}
                  onChange={(e) => void vaihdaLuovutus(e.target.value || null)} /></label>
              ) : (
                <>{tr("koh.luovutettu")} {naytaPvm(kohde.luovutettu_pvm) ?? tr("koh.paivaa_ei_tiedeta")}</>
              )}
            </div>
          )}
        </td>
        <td className="td--oikea">
          <button className="nappi nappi--kevyt" onClick={avaa} aria-expanded={auki}>
            {auki ? tr("hl.sulje") : tr("koh.tarpeet")}
          </button>
          {/* 278 (Toni 3.11.2026 klo 15.10 kohta 4): pääkäyttäjä arkistoi ja palauttaa. Mitään ei poisteta. */}
          {saaArkistoida && (
            <button className="nappi nappi--kevyt" disabled={tallennetaan} style={{ marginLeft: 6 }}
                    data-arkistoi={kohde.id} data-arkistoitu-tila={kohde.arkistoitu ? "1" : "0"}
                    title={tr(kohde.arkistoitu ? "koh.palauta_title" : "koh.arkistoi_title")}
                    onClick={() => void vaihdaArkistointi(!kohde.arkistoitu)}>
              {tr(kohde.arkistoitu ? "koh.palauta" : "koh.arkistoi")}
            </button>
          )}
        </td>
      </tr>

      {auki && (
        <tr className="tr--laajennus">
          <td colSpan={8}>
            <div className="laajennus">
              {saaMuokata && <KohteenTunnus kohde={kohde} paivita={paivita} />}
              <div data-kohteen-asiakkaat={kohde.id}>
                <KohteenAsiakkaat kohde={kohde} saaMuokata={saaMuokata} paivita={paivita} />
              </div>

              {/* Resurssitarve on työvoimaa, ei työtä — tilaajalta pois kokonaan (Toni 26.9.2026). */}
              {!tilaaja && <>
              <h3>{tr("koh.resurssitarve")}</h3>

              {tarpeet === null && <p className="dim">{tr("ltar.ladataan")}</p>}
              {tarpeet?.length === 0 && (
                <p className="dim">
                  {tr("koh.ei_tarpeita")}
                </p>
              )}

              {tarpeet && tarpeet.length > 0 && (
                <ul className="kohdelista">
                  {tarpeet.map((t) => (
                    <li key={t.id}>
                      <span><strong>{t.maara}</strong> × {t.osaamisluokka}</span>
                      {/* Paivays normaalilla fontilla (Toni 21.9.): tasalevyinen
                          kuuluu tunnisteille ja luvuille joita verrataan pystysuunnassa. */}
                      <span className="pvmvali">
                        {naytaPvm(t.alkaa)} – {naytaPvm(t.paattyy) ?? <em className="dim">{tr("koh.toistaiseksi")}</em>}
                      </span>
                      {/* Vaihe 1: tarjokas on LEIKKAUS eikä osaamisluokan koko.
                          Neljä ryhmää, neljä eri tekoa — ja «ei kelpaa nyt» on
                          este joka on voimassa tänään, ei muistutus. */}
                      {/* ⛔ EROTIN JOKAISEN LUVUN VÄLISSÄ (Markku 21.9.): siruilla
                          ei ollut väliä, ja «sidottu 4 / 3kelpoisia 04 sidottua»
                          luettiin yhtenä lukuna. Avoinna X / N on ensimmäinen,
                          koska se vastaa kysymykseen «tarvitaanko vielä väkeä»
                          (Toni 22.9.) — myös tarpeella jolla ei ole vaatimuksia. */}
                      <span className="tarjokkaat" data-tarve={t.id}
                            data-avoinna={t.avoinna ?? ""} data-sidottuja={t.sidottuja ?? ""}>
                        {(() => {
                          const osat: ReactNode[] = [
                            <Tilasana key="av" laji={(t.avoinna ?? 0) === 0 ? "kunnossa" : "varoitus"}>
                              {tr("koh.avoinna", { a: t.avoinna ?? t.maara, n: t.maara })}
                            </Tilasana>,
                            <span key="si">{tr("koh.sidottu_n", { n: t.sidottuja ?? 0 })}</span>,
                          ];
                          if (t.vaatimukset && t.vaatimukset.length > 0) {
                            osat.push(
                              <Tilasana key="ke" laji={(t.tarjokkaita ?? 0) >= t.maara ? "kunnossa" : "vika"}>
                                {tr("koh.kelpoisia", { n: t.tarjokkaita ?? 0 })}
                              </Tilasana>);
                            if ((t.sidottuja_vajain ?? 0) > 0) osat.push(
                              <Tilasana key="va" laji="vika">
                                {tr("koh.sidottu_vajain", { n: t.sidottuja_vajain ?? 0 })}
                              </Tilasana>);
                            if ((t.ei_kelpaa_nyt ?? 0) > 0) osat.push(
                              <Tilasana key="en" laji="vika">{tr("koh.n_ei_kelpaa_nyt", { n: t.ei_kelpaa_nyt ?? 0 })}</Tilasana>);
                            if ((t.uusittava ?? 0) > 0) osat.push(
                              <Tilasana key="uu" laji="varoitus">{tr("koh.n_uusittava", { n: t.uusittava ?? 0 })}</Tilasana>);
                          } else {
                            osat.push(<span key="ev" className="dim">{tr("koh.ei_vaatimuksia_lyh")}</span>);
                          }
                          osat.push(<span key="os" className="dim">{tr("koh.osaajia_luokassa", { n: t.osaajia ?? 0 })}</span>);
                          if ((t.kohteessa_sitomatta ?? 0) > 0) osat.push(
                            <span key="ks" className="dim" data-sitomattomat={t.kohteessa_sitomatta}
                                  title={tr("koh.sitomatta_title")}>
                              {tr("koh.kohteessa_sitomatta", { n: t.kohteessa_sitomatta ?? 0 })}
                            </span>);
                          return osat.map((o, i) => <span key={i}>{i > 0 && " · "}{o}</span>);
                        })()}
                      </span>
                      {/* Nappirivi oikeaan reunaan, samaan linjaan laskurien ja
                          vaatimusrivien kanssa (Toni 21.9.). `margin-left: auto`
                          vie sen reunaan ilman ettei rivin muu sisalto siirry.

                          ⛔ «Poista tarve» EI ole «Poista». Ne osuvat nyt samaan
                          sarakkeeseen kuin vaatimusrivin poisto, ja samanniminen
                          nappi paallekkain tarkoittaisi etta vaara painallus
                          poistaa koko tarpeen vaatimuksen sijaan. Nimi erottaa
                          teot, vahvistus antaa perumismahdollisuuden. */}
                      <span className="tarve__toiminnot">
                        {saaMuokata && (
                          <button className="nappi nappi--kevyt"
                                  onClick={() => { setLisaysRivi(lisaysRivi === t.id ? null : t.id);
                                                   setLisaysTyyppi(""); setLisaysLuku(""); }}>
                            {lisaysRivi === t.id ? tr("isom.peru") : tr("koh.plus_vaatimus")}
                          </button>
                        )}
                        {(t.tarjokkaat?.length ?? 0) > 0 && (
                          <button className="nappi nappi--kevyt"
                                  onClick={() => setAuki2(auki2 === t.id ? null : t.id)}>
                            {auki2 === t.id ? tr("koh.piilota_tarjokkaat") : tr("koh.tarjokkaat_n", { n: t.tarjokkaat!.length })}
                          </button>
                        )}
                        {saaMuokata && (
                          <button className="nappi nappi--kevyt" disabled={tallennetaan}
                                  onClick={() => void poistaKokoTarve(t)}>{tr("koh.poista_tarve")}</button>
                        )}
                      </span>
                      {/* Vaatimukset allekkain omalle riville (Toni 20.9.):
                          rivin sisällä ne olivat sirujen ja nappien jono, josta ei
                          erottanut mikä luku kuuluu mihinkin vaatimukseen. Yksi rivi
                          per vaatimus vastaa kysymykseen «miksi vain yksi tarjokas»
                          suoraan: nimi, montako heistä sitä vaaditaan, ja montako
                          täyttää sen. Poisto on sillä rivillä jota se koskee. */}
                      {t.vaatimukset && t.vaatimukset.length > 0 && (
                        <ul className="tarvevaatimukset">
                          {t.vaatimukset.map((v) => (
                            <li key={v.id}>
                              <span className="tarvevaatimus__nimi">
                                {v.nimi}
                                {v.lukumaara < Number(t.maara) && (
                                  <span className="dim"> — {tr("koh.n_maarasta", { n: v.lukumaara, m: t.maara })}</span>
                                )}
                              </span>
                              <Tilasana laji={v.tayttaa >= v.lukumaara ? "kunnossa" : "vika"}>
                                {tr("koh.tayttaa", { n: v.tayttaa, m: v.lukumaara })}
                              </Tilasana>
                              {v.kelpaa_tanaan < v.tayttaa && (
                                <Tilasana laji="varoitus">{tr("koh.tanaan_n", { n: v.kelpaa_tanaan })}</Tilasana>
                              )}
                              {saaMuokata && (
                                <button className="nappi nappi--kevyt" disabled={tallennetaan}
                                        title={tr("koh.poista_vaatimus_title", { nimi: v.nimi })}
                                        onClick={() => void poistaRiviVaatimus(t, v)}>
                                  {tr("liite.poista")}
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {lisaysRivi === t.id && (
                        <div className="allokointi">
                          <label>
                            {tr("koh.patevyys")}
                            <select value={lisaysTyyppi}
                                    onChange={(e) => setLisaysTyyppi(e.target.value ? Number(e.target.value) : "")}>
                              <option value="">{tr("hl.valitse")}</option>
                              {ptyypit.map((p2) => <option key={p2.id} value={p2.id}>{p2.nimi}</option>)}
                            </select>
                          </label>
                          <label>
                            {tr("koh.montako", { n: t.maara })}
                            <input type="number" min={1} max={Number(t.maara)} step={1} size={3}
                                   placeholder={String(Number(t.maara))}
                                   value={lisaysLuku} onChange={(e) => setLisaysLuku(e.target.value)} />
                            <span className="hint">{tr("koh.tyhja_kaikilta", { n: Number(t.maara) })}</span>
                          </label>
                          <button className="nappi nappi--paa" disabled={tallennetaan || lisaysTyyppi === ""}
                                  onClick={() => void lisaaRiville(t)}>{tr("koh.lisaa")}</button>
                        </div>
                      )}
                      {/* Päätös 13 (Toni 30.9.2026): kohteessa mutta ei tähän tehtävään sidotut NIMELTÄ, ja sitominen
                          samalta riviltä — vain tehtävä vaihtuu, tunnit ja huomiot säilyvät. Peruste henkilöittäin. */}
                      {(t.sitomattomat?.length ?? 0) > 0 && (
                        <ul className="tarvevaatimukset sidotut" data-sitomattomat-lista={t.id}>
                          {t.sitomattomat!.map((h) => (
                            <li key={h.resurssi_id} data-sitomaton={h.resurssi_id}>
                              <span>{tr("koh.sitomaton_rivi", { nimi: h.nimi, pv: h.paivia, h: h.tunnit })}</span>
                              {saaMuokata && (
                                <>
                                  {liitaPerusteRivi === `${t.id}:${h.resurssi_id}` && (
                                    <Estelaatikko esteAttr="data-liita-este" perusteAttr="data-liita-peruste"
                                                  selite={tr("koh.liita_selite")}
                                                  rivit={[{ avain: h.resurssi_id, nimi: h.nimi, puuttuu: liitaPuuttuu ?? tr("koh.pakollinen_patevyys") }]}
                                                  peruste={liitaPeruste} setPeruste={setLiitaPeruste}
                                                  placeholder={tr("koh.liita_placeholder")} />
                                  )}
                                  <button className="nappi nappi--kevyt" disabled={tallennetaan}
                                          title={tr("koh.liita_title")}
                                          onClick={() => void liitaAllokaatiot(t, h.resurssi_id, h.nimi)}>
                                    {liitaPerusteRivi === `${t.id}:${h.resurssi_id}` ? tr("koh.sido_tehtavaan_peruste") : tr("koh.sido_tehtavaan")}
                                  </button>
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* Sidotut tähän tarpeeseen: kuka, mille välille, ja purku
                          samalta riviltä (sidontalomake, Toni 23.9.). */}
                      {(t.sidonnat?.length ?? 0) > 0 && (
                        <ul className="tarvevaatimukset sidotut" data-sidotut={t.id}>
                          {t.sidonnat!.map((s) => (
                            <li key={s.id} data-sidonta={s.id}>
                              <span className="tarvevaatimus__nimi">
                                <strong>{s.nimi}</strong>
                                <span className="dim"> · {tr("koh.sidottu_sana")} {naytaPvm(s.alkaa)}–{s.paattyy ? naytaPvm(s.paattyy) : tr("koh.toistaiseksi")}</span>
                                {s.peruste && <span className="dim" title={s.peruste}> · {tr("koh.peruste_kirjattu")}</span>}
                              </span>
                              {saaSitoa && (
                                <button className="nappi nappi--kevyt" disabled={tallennetaan}
                                        onClick={() => void puraSidonta(s)}>{tr("koh.pura")}</button>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {auki2 === t.id && (() => {
                        /* Täynnä vasta kun `maara` sidontaa kattaa KOKO jakson: täyttö
                           lasketaan päivä kerrallaan (159), joten osajaksoille voi
                           yhä mahtua. Muuten palvelin torjuu nimetysti (tarve_taynna). */
                        const kokonaan = (t.sidonnat ?? []).filter((s) =>
                          s.alkaa <= t.alkaa && (s.paattyy === null || (t.paattyy !== null && s.paattyy >= t.paattyy))).length;
                        const taynna = kokonaan >= Number(t.maara);
                        return (
                        <div className="tarjokaslista">
                          {taynna && (
                            <p className="huom" role="status">
                              {tr("koh.tarve_taynna")}
                            </p>
                          )}
                          <ul>
                            {t.tarjokkaat!.map((x) => {
                              const tila = TARJOKAS_TILA[x.tila] ?? TARJOKAS_TILA.ei_tayta!;
                              const vapaa = vapaaAlkaen(t, x.sidottu ?? []);
                              const avain = `${t.id}:${x.henkilo_id}`;
                              return (
                              <li key={x.henkilo_id} className="tarjokas" data-tarjokas={x.henkilo_id}>
                                {/* Nimi omalle rivilleen, tila sanana, puutteet
                                    allekkain (Toni 23.9.): yhtenä putkena rivi oli
                                    luettavissa vain vasemmalta oikealle kokonaan. */}
                                <div className="tarjokas__yla">
                                  <strong className="tarjokas__nimi">{x.nimi}</strong>
                                  <Tilasana laji={tila.laji}>{tila.sana()}</Tilasana>
                                  {saaSitoa && (
                                    <button className="nappi nappi--kevyt" disabled={tallennetaan || taynna}
                                            aria-expanded={sidontaRivi === avain}
                                            onClick={() => avaaSidonta(t, x.henkilo_id)}>
                                      {sidontaRivi === avain ? tr("isom.peru") : tr("koh.sido_avaa")}
                                    </button>
                                  )}
                                </div>
                                <ul className="tarjokas__puutteet">
                                  {/* Eräpäivä kuuluu sille riville jolla teko on:
                                      «puuttuu Tulityökortti» ilman päivää ei kerro
                                      ehtiikö sen hankkia. */}
                                  {x.tila !== "tayttaa" && <li>{erapaiva(t)}</li>}
                                  {/* ⛔ KOLME SYYTÄ (sääntö 61): ei korttia (hankittava) ·
                                      umpeutunut (uusittava) · voimassaolo kirjaamatta. */}
                                  {(x.puuttuu?.length ?? 0) > 0 && (
                                    <li>{tr("koh.patevyydet_kp")}{" "}
                                      {x.puuttuu!.map((v, i) => (
                                        <span key={v.nimi}>
                                          {i > 0 && ", "}
                                          {v.nimi}
                                          <span className="dim">
                                            {" "}{!v.on_kortti ? tr("koh.ei_korttia")
                                              : v.voimassa_asti ? tr("koh.umpeutui", { pvm: naytaPvm(v.voimassa_asti) ?? "" })
                                              : tr("koh.voimassaolo_kirjaamatta")}
                                          </span>
                                        </span>
                                      ))}
                                    </li>
                                  )}
                                  {/* 147: perehdytys EI estä sidontaa — se on tehtävä
                                      ennen ensimmäistä työpäivää. Oma rivinsä, koska
                                      estävä ja tehtävä eivät saa näyttää samalta. */}
                                  {(x.perehdytys_puuttuu?.length ?? 0) > 0 && (
                                    <li data-perehdytys={x.henkilo_id}>
                                      {tr("koh.perehdytys_kp", { lista: x.perehdytys_puuttuu!.join(", ") })}
                                      {x.perehdytys_mennessa && <> {tr("koh.ennen", { pvm: naytaPvm(x.perehdytys_mennessa) ?? "" })}</>}
                                    </li>
                                  )}
                                  {(x.sidottu?.length ?? 0) > 0 && (
                                    <li data-vapaa={typeof vapaa === "string" ? vapaa : ""}>
                                      {tr("koh.sidottu_iso")}{" "}
                                      {x.sidottu!.map((s, i) => (
                                        <span key={i}>
                                          {i > 0 && ", "}
                                          {s.tama ? tr("koh.tahan") : tr("koh.kohteeseen", { kohde: s.kohde })}{" "}
                                          {naytaPvm(s.alkaa)}–{s.paattyy ? naytaPvm(s.paattyy) : tr("koh.toistaiseksi")}
                                        </span>
                                      ))}
                                      {vapaa === "ei" ? <> · {tr("koh.ei_vapaita")}</>
                                        : vapaa ? <> · <strong>{tr("koh.vapaa_alkaen", { pvm: naytaPvm(vapaa) ?? "" })}</strong></>
                                        : null}
                                    </li>
                                  )}
                                  {x.allokoitu_pv > 0 && (
                                    <li className="dim">{tr("koh.allokoitu_pv", { n: x.allokoitu_pv })}</li>
                                  )}
                                  {x.peruste && <li className="dim" title={x.peruste}>{tr("koh.peruste_kirjattu_kp", { peruste: x.peruste })}</li>}
                                </ul>

                                {sidontaRivi === avain && (
                                  <div className="sidontalomake" data-sidontalomake={avain}>
                                    <fieldset className="sidontalomake__laajuus">
                                      <legend>{tr("koh.sidotaan")}</legend>
                                      <label className="valinta">
                                        <input type="radio" name={`laajuus-${avain}`} checked={laajuus === "koko"}
                                               onChange={() => setLaajuus("koko")} />
                                        {tr("koh.koko_jaksolle")} {naytaPvm(t.alkaa)}–{t.paattyy ? naytaPvm(t.paattyy) : tr("koh.toistaiseksi")}
                                      </label>
                                      <label className="valinta">
                                        <input type="radio" name={`laajuus-${avain}`} checked={laajuus === "osa"}
                                               onChange={() => setLaajuus("osa")} />
                                        {tr("koh.osalle_jaksosta")}
                                      </label>
                                    </fieldset>
                                    {laajuus === "osa" && (
                                      <div className="sidontalomake__vali">
                                        <label>
                                          {tr("mit.alkaa")}
                                          <input type="date" value={sAlkaa} min={t.alkaa} max={t.paattyy ?? undefined}
                                                 onChange={(e) => setSAlkaa(e.target.value)} />
                                        </label>
                                        <label>
                                          {tr("koh.paattyy_mukaan")}
                                          <input type="date" value={sPaattyy} min={sAlkaa || t.alkaa} max={t.paattyy ?? undefined}
                                                 onChange={(e) => setSPaattyy(e.target.value)} />
                                        </label>
                                      </div>
                                    )}
                                    {/* Tuntia/päivä kapeana kenttänä yksikön kanssa
                                        (Toni 23.9.) — label on flex-sarake, joten
                                        ilman leveysrajaa syöte venyi koko riville. */}
                                    <label className="kapea-kentta">
                                      {tr("koh.tyopaiva")}
                                      <span className="kapea-kentta__rivi">
                                        <input type="number" min={1} max={24} step={0.5} inputMode="decimal"
                                               value={tuntia} onChange={(e) => setTuntia(e.target.value)} />
                                        <span>{tr("koh.h_pv")}</span>
                                      </span>
                                    </label>
                                    {/* 145: peruste kysytään vasta kun palvelin on
                                        nimennyt mikä puuttuu. */}
                                    {perusteRivi === avain && (
                                      <Estelaatikko esteAttr="data-sidonta-este" perusteAttr="data-sidonta-peruste"
                                                    selite={tr("koh.sidonta_selite")}
                                                    rivit={[{ avain: x.henkilo_id, nimi: x.nimi,
                                                              puuttuu: (x.puuttuu ?? []).map((v) => v.nimi).join(", ") || tr("koh.pakollinen_patevyys") }]}
                                                    peruste={peruste} setPeruste={setPeruste}
                                                    placeholder={tr("koh.sidonta_placeholder")} />
                                    )}
                                    <button className="nappi nappi--paa"
                                            disabled={tallennetaan || (perusteRivi === avain && !peruste.trim())}
                                            onClick={() => void sido(t, x.henkilo_id, x.nimi)}>
                                      {perusteRivi === avain ? tr("koh.sido_peruste") : tr("koh.sido")}
                                    </button>
                                  </div>
                                )}
                              </li>
                              );
                            })}
                          </ul>
                          {/* «Ei täytä vaatimuksia» EI ole piilotettu: työnjohtaja saa
                              sitoa hänet tietoisesti (kortti voi olla tulossa), mutta
                              rivi sanoo tilan ääneen. */}
                          <p className="dim">
                            {tr("koh.sidonta_huom")}
                          </p>
                        </div>
                        );
                      })()}
                      {t.vanhenevat && t.vanhenevat.length > 0 && (
                        <ul className="vanhenevat">
                          {t.vanhenevat.map((v, i) => (
                            <li key={i}>
                              <Tilasana laji={v.ryhma === "ei_kelpaa_nyt" ? "vika" : "varoitus"}>
                                {v.ryhma === "ei_kelpaa_nyt" ? tr("koh.ei_kelpaa_nyt") : tr("koh.uusittava")}
                              </Tilasana>{" "}
                              {v.nimi} · {v.vaatimus} ·{" "}
                              <span className="mono">{naytaPvm(v.voimassa_asti) ?? "—"}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* Uuden tarpeen lomake järjestyksessä jossa se täytetään (Markku 21.9.):
                  tarpeen tiedot → pätevyysvaatimukset → tallennus. Ennen «Lisää tarve»
                  oli ylimmällä rivillä, ja vaatimusten lisäämisen jälkeen tallennus
                  jäi niiden yläpuolelle piiloon. */}
              {saaMuokata && (
                <div className="uusitarve" data-uusi-tarve={kohde.id}>
                <h3>{tr("koh.uusi_tarve")}</h3>
                <div className="allokointi">
                  <label>
                    {tr("mit.osaaminen")}
                    <select value={luokkaId}
                            onChange={(e) => setLuokkaId(e.target.value ? Number(e.target.value) : "")}>
                      <option value="">{tr("hl.valitse")}</option>
                      {luokat.map((l) => <option key={l.id} value={l.id}>{l.nimi}</option>)}
                    </select>
                  </label>
                  <label>
                    {tr("mit.henkiloa")}
                    {/* ⚠ Tassa luki `min={0.5} step={0.5}`, eli puolikkaat olivat
                        kirjoitettu valinta eivatka unohdus. Tarve on paikkoja. */}
                    <input type="number" min={1} step={1} size={4} value={maara}
                           onChange={(e) => setMaara(e.target.value)} />
                  </label>
                  <label>
                    {tr("mit.alkaa")}
                    <input type="date" value={alkaa} onChange={(e) => setAlkaa(e.target.value)} />
                  </label>
                  <label>
                    {tr("mit.paattyy")}
                    <input type="date" value={paattyy} onChange={(e) => setPaattyy(e.target.value)} />
                  </label>
                </div>
                <p className="ikkuna__vihje">
                  {tr("koh.tyhja_loppu")}
                </p>

                {/* Vaatimukset syntyvät TARPEEN mukana: «neljä hitsaajaa,
                    kolmella tulityökortti» on yksi teko eikä kahta. Tyhjä on
                    kelvollinen — tarve ilman vaatimuksia on tavallinen, eikä
                    lomake saa pakottaa keksimään sellaista. */}
                <div className="vaatimusrivit">
                  <div className="vaatimusrivit__otsikko">
                    {tr("koh.tyomaa_vaatii_tarpeelta")}
                    <span className="dim"> · {tr("koh.tyomaa_vaatii_hint")}</span>
                  </div>
                  {uudetVaatimukset.map((v, i) => (
                    <div className="allokointi" key={i}>
                      <label>
                        {tr("koh.patevyys")}
                        <select value={v.tyyppi}
                                onChange={(e) => setUudetVaatimukset(uudetVaatimukset.map((x, j) =>
                                  j === i ? { ...x, tyyppi: e.target.value ? Number(e.target.value) : "" } : x))}>
                          <option value="">{tr("hl.valitse")}</option>
                          {ptyypit.map((p2) => <option key={p2.id} value={p2.id}>{p2.nimi}</option>)}
                        </select>
                      </label>
                      <label>
                        {tr("koh.montako", { n: maara || "?" })}
                        <input type="number" min={1} step={1} size={3} value={v.luku}
                               onChange={(e) => setUudetVaatimukset(uudetVaatimukset.map((x, j) =>
                                 j === i ? { ...x, luku: e.target.value } : x))} />
                      </label>
                      <button className="nappi nappi--kevyt"
                              onClick={() => setUudetVaatimukset(uudetVaatimukset.filter((_, j) => j !== i))}>
                        {tr("liite.poista")}
                      </button>
                    </div>
                  ))}
                  <button className="nappi"
                          onClick={() => setUudetVaatimukset([...uudetVaatimukset, { tyyppi: "", luku: maara || "1" }])}>
                    {tr("koh.lisaa_patevyysvaatimus")}
                  </button>
                </div>

                {/* Tallennus viimeisenä: se tallentaa tarpeen JA yllä luetellut
                    vaatimukset, ja nappi sanoo montako. */}
                <div className="uusitarve__tallennus">
                  <button className="nappi nappi--paa" disabled={tallennetaan || luokkaId === ""}
                          onClick={() => void lisaa()}>
                    {tallennetaan ? tr("pu.tallennetaan") : (() => {
                      const n = uudetVaatimukset.filter((v) => v.tyyppi !== "").length;
                      return n > 1 ? tr("koh.lisaa_tarve_ja_n", { n }) : n === 1 ? tr("koh.lisaa_tarve_ja_1") : tr("koh.lisaa_tarve");
                    })()}
                  </button>
                  {luokkaId === "" && <span className="dim">{tr("koh.valitse_osaaminen")}</span>}
                </div>
                </div>
              )}
              </>}

              {perehdytykset && <Perehdytykset kohde={kohde} saaMuokata={saaMuokata} />}

              <h3>{tr("koh.patevyysvaatimukset")}</h3>

              {vaatimukset === null && <p className="dim">{tr("ltar.ladataan")}</p>}
              {vaatimukset?.length === 0 && (
                <p className="dim">
                  {tr("koh.ei_vaatimuksia")}
                </p>
              )}

              {ryhmat.map((g) => {
                const vapaat = g.osaajat.filter((o) => o.saatavuus === "vapaa").length;
                return (
                  <div key={g.id} className="vaatimus">
                    <div className="vaatimus__otsikko">
                      <span><strong>{g.lukumaara}</strong> × {g.patevyys}</span>
                      {/* Tilaajalle vain vaatimus: osaajat ja «vapaana» ovat ihmisiä, eikä API lähetä
                          henkilörivejä — «0 osaajaa» ja «Ei ketään…» olisivat väitteitä (26.9.2026). */}
                      {!tilaaja && <span className="mono dim">{tr("koh.osaajaa_vapaana", { n: g.osaajat.length, v: vapaat })}</span>}
                      {saaMuokata && (
                        <button className="nappi nappi--kevyt" disabled={tallennetaan}
                                onClick={() => void poistaVaatimus(g.id)}>{tr("liite.poista")}</button>
                      )}
                    </div>
                    {tilaaja ? null : g.osaajat.length === 0 ? (
                      <p className="dim">{tr("koh.ei_ketaan")}</p>
                    ) : g.osaajat[0]!.henkilo === undefined ? null : (
                      /* Nimetön lista ei kertoisi mitään lisää (26.9.2026). */
                      <ul className="osaajat">
                        {g.osaajat.map((o) => (
                          <li key={o.henkilo_id}>
                            <span>{o.lyhenne} · {o.henkilo}</span>
                            <span className={`saat saat--${o.saatavuus}`}>
                              {nayttonimiPieni("saatavuus", o.saatavuus)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}

              {/* ⛔ Vanhoja kohdetason vaatimuksia EI kohdisteta automaattisesti:
                  kohde voi kantaa monta tarveriviä, eikä mikään kentässä kerro
                  kumpaa vaatimus tarkoitti. Arvaus näyttäisi samalta kuin
                  ihmisen päätös — siksi ne luetellaan ja ihminen kohdistaa. */}
              {kohdistamattomat.length > 0 && (
                <div className="paatokset" data-kohdistamattomat={kohdistamattomat.length}>
                  <div className="panel__head">
                    <h3>{tr("koh.kohdistamattomat")}</h3>
                    <span className="hint">
                      {tr("koh.kohdistamattomat_hint", { n: kohdistamattomat.length })}
                    </span>
                  </div>
                  <p className="dim">
                    {tr("koh.kohdistamattomat_selite")}
                  </p>
                  <ul>
                    {kohdistamattomat.map((v) => {
                      // Kolme eri tekoa, eikä niitä erota ilman lukuja:
                      //  1 mahtuu johonkin  → kiinnitä oikealle riville
                      //  2 ei mahdu mihinkään mutta mahtuu kohteeseen → jaa tai pienennä
                      //  3 suurempi kuin koko kohteen tarve → luvun on muututtava ensin
                      const mahtuvat = v.tarpeet.filter((t2) => t2.mahtuu);
                      const mahdoton = v.tarve_yhteensa != null && v.lukumaara > v.tarve_yhteensa;
                      return (
                        <li key={v.id}>
                          <Tilasana laji={mahdoton ? "vika" : "ei_mitattu"}>
                            {mahdoton ? tr("koh.ei_mahdu") : tr("koh.kohdistamaton")}
                          </Tilasana>{" "}
                          {v.patevyys}
                          <span className="dim"> · {tr("koh.kirjattu_lukuna", { n: v.lukumaara })}</span>
                          {v.tarpeet.length === 0 ? (
                            <span className="dim"> · {tr("koh.ei_tarverivit")}</span>
                          ) : mahdoton ? (
                            <span className="dim">
                              {" · "}{tr("koh.koko_tarve_on", { n: v.tarve_yhteensa ?? "" })}
                            </span>
                          ) : (
                            <span className="dim">
                              {" · "}{tr("koh.mahtuu")}{" "}
                              {mahtuvat.length === 0
                                ? tr("koh.ei_yhdellekaan")
                                : mahtuvat.map((t2) => `${t2.osaamisluokka} ${t2.maara}`).join(" · ")}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Alalaidan lomake POISTETTU 19.9.2026: vaatimus syntyy nyt tarpeen
                  mukana tai rivin «+ vaatimus» -napista. Kaksi reittiä samaan
                  asiaan ajautuu erilleen, ja «Koskee tarvetta» -valikko oli
                  olemassa vain siksi että vaatimus lisättiin erillään tarpeesta.
                  Uutta KOHDISTAMATONTA ei voi enää syntyä — reitti vaatii
                  tarverivin — joten kohdistamaton on migraatiojäämä eikä
                  ominaisuus (Toni 19.9.). */}

              {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── uusi kohde ─────────────────────────────────────────── */

function UusiKohde({ peruuta, valmis }: { peruuta: () => void; valmis: () => Promise<void> }) {
  const [tunnus, setTunnus] = useState("");
  const [nimi, setNimi] = useState("");
  const [asiakas, setAsiakas] = useState("");
  const [sijainti, setSijainti] = useState("");
  const [laji, setLaji] = useState<"projekti" | "jatkuva">("projekti");
  const [alkaa, setAlkaa] = useState("");
  const [paattyy, setPaattyy] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  async function tallenna(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null);
    setTallennetaan(true);
    try {
      await api.luoKohde({
        tunnus: tunnus.trim(), nimi: nimi.trim(), laji,
        ...(asiakas.trim() && { asiakas: asiakas.trim() }),
        ...(sijainti.trim() && { sijainti: sijainti.trim() }),
        ...(alkaa && { alkaa }),
        ...(paattyy && { paattyy }),
      });
      await valmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
      setTallennetaan(false);
    }
  }

  return (
    <section className="panel lomake">
      <div className="panel__head">
        <h2>{tr("koh.uusi_kohde")}</h2>
        <span className="hint">{tr("koh.jatkuva_hint")}</span>
      </div>
      <form className="lomake__kentat" onSubmit={tallenna}>
        <label>
          {tr("koh.tunnus")}
          <input value={tunnus} onChange={(e) => setTunnus(e.target.value)}
                 required autoFocus size={10} placeholder="K-101" />
        </label>
        <label className="kasvaa">
          {tr("koh.nimi")}
          <input value={nimi} onChange={(e) => setNimi(e.target.value)} required
                 placeholder={tr("koh.nimi_placeholder")} />
        </label>
        <label className="kasvaa">
          {tr("koh.asiakas")}
          <input value={asiakas} onChange={(e) => setAsiakas(e.target.value)} />
        </label>
        <label>
          {tr("koh.sijainti")}
          <input value={sijainti} onChange={(e) => setSijainti(e.target.value)} size={14} />
        </label>
        <label>
          {tr("koh.laji")}
          <select value={laji} onChange={(e) => setLaji(e.target.value as "projekti" | "jatkuva")}>
            <option value="projekti">{tr("koh.rajattu_projekti")}</option>
            <option value="jatkuva">{tr("koh.jatkuva_sopimus")}</option>
          </select>
        </label>
        <label>
          {tr("mit.alkaa")}
          <input type="date" value={alkaa} onChange={(e) => setAlkaa(e.target.value)}
                 required={laji === "projekti"} />
        </label>
        <label>
          {tr("mit.paattyy")}
          <input type="date" value={paattyy} onChange={(e) => setPaattyy(e.target.value)} />
        </label>

        <div className="lomake__toiminnot">
          <button type="submit" className="nappi nappi--paa" disabled={tallennetaan}>
            {tallennetaan ? tr("pu.tallennetaan") : tr("ltar.tallenna")}
          </button>
          <button type="button" className="nappi nappi--kevyt" onClick={peruuta}>
            {tr("hl.peruuta")}
          </button>
        </div>

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      </form>
    </section>
  );
}

/* ── apurit ─────────────────────────────────────────────── */



/** ISO-viikkonumero. */
function viikkoNro(pvm: Date): number {
  const d = new Date(Date.UTC(pvm.getUTCFullYear(), pvm.getUTCMonth(), pvm.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const vuodenAlku = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - vuodenAlku.getTime()) / 86400000 + 1) / 7);
}

/* ── kohteen asiakkaat (149) ────────────────────────────────
   Toni 20.–21.9.2026. Vaatimus oli nimenomaan se, että OLEMASSA olevan
   kohteen voi sitoa asiakkaaseen — muunnos 149 sai kytkeä vain tasan
   yhden tarkan nimiosuman, ja mitattuna se oli 1 kohde 13:sta. Loput 12
   ovat tilassa «ei asetettu», ja tämä on se paikka jossa ihminen kytkee
   ne.

   ⛔ ROOLI EI OLE PÄÄSY. Tämä lohko ei kosketa käyttäjien näkyvyyteen:
   tilaajaksi lisääminen ei anna kenellekään pääsyä eikä roolin vaihto
   ota sitä pois. Se sanotaan myös ruudulla, koska «asiakkaan lisääminen»
   kuulostaa siltä että se avaisi jotain.

   Muutoksesta jää kirjaus muutoslokiin (tekijä, aika, vanha ja uusi
   arvo) audit-liipaisimella — omaa lokia ei ole, koska toinen mekanismi
   ajautuisi erilleen. */
function KohteenAsiakkaat({
  kohde, saaMuokata, paivita,
}: {
  kohde: Kohde; saaMuokata: boolean; paivita: () => Promise<void>;
}) {
  const [rivit, setRivit] = useState<KohteenAsiakas[] | null>(null);
  const [rekisteri, setRekisteri] = useState<Asiakas[] | null>(null);
  /* 150: roolit kannasta. Vakiolista selaimessa olisi toinen totuus,
     ja se olisi se joka on väärässä hiljaa kun rooli lisätään. */
  const [roolit, setRoolit] = useState<Asiakasrooli[]>([]);
  const [valittu, setValittu] = useState<number | "">("");
  const [rooli, setRooli] = useState<AsiakasRooli>("tilaaja");
  const [uusiNimi, setUusiNimi] = useState("");
  const [uusiAuki, setUusiAuki] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);

  const hae = useCallback(async () => {
    try {
      /* Rekisteri vain muokkaajalle: GET /api/asiakkaat on paakayttaja/suunnittelija (sama lista kuin
         saaMuokata), ja muille 403 kaatoi koko asiakasosion — mitattu 26.9.2026 tilaajalla, kun
         Työkohteet alkoi piirtyä. Valintalista on ainoa lukija. */
      const [r, a, rl] = await Promise.all([
        api.kohteenAsiakkaat(kohde.id),
        saaMuokata ? api.asiakkaat() : Promise.resolve([] as Asiakas[]),
        api.asiakasroolit(),
      ]);
      setRivit(r); setRekisteri(a); setRoolit(rl); setVirhe(null);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.asiakastiedot"));
    }
  }, [kohde.id, saaMuokata]);

  useEffect(() => { if (rivit === null) void hae(); }, [rivit, hae]);
  /* 81 (Toni 5.10.2026 klo 15.33): jos rekisterissä on kohteen asiakastekstin kanssa samanniminen asiakas (normalisoituna:
     pienet kirjaimet, välilyönnit ja yhtiömuoto pois), se on valmiiksi valittuna — Markun tapaus «Biovoima» ↔ rekisterin Biovoima. */
  useEffect(() => {
    if (valittu !== "" || !rekisteri || !kohde.asiakas || (rivit && rivit.length > 0)) return;
    const n = (x: string) => x.toLowerCase().replace(/\b(oy|ab|oyj|ltd|ky|tmi)\b/g, "").replace(/[^a-z0-9äöå]/g, "");
    const osuma = rekisteri.find((a) => n(a.nimi) === n(kohde.asiakas ?? ""));
    if (osuma) setValittu(osuma.id);
  }, [rekisteri, kohde.asiakas, rivit, valittu]);

  /* Passivoitu rooli näkyy yhä nimellään olemassa olevalla rivillä —
     vain valintalista lyhenee. Tuntematon tunnus näytetään sellaisenaan
     eikä käännetä tyhjäksi: «—» olisi väite siitä ettei roolia ole. */
  const roolinNimi = (t: AsiakasRooli) =>
    roolit.find((r) => r.tunnus === t)?.nimi ?? t;

  return (
    <>
      <h3>{tr("koh.asiakas")}</h3>

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {rivit?.length === 0 && (
        <p className="dim">
          {tr("koh.ei_liitetty")}
          {kohde.asiakas && <> {tr("koh.oma_teksti_on", { teksti: kohde.asiakas })}</>}
        </p>
      )}

      {rivit && rivit.length > 0 && (
        <ul className="tarvevaatimukset">
          {rivit.map((r) => (
            <li key={r.id}>
              <strong>{r.nimi}</strong>
              {r.y_tunnus && <span className="dim"> · {r.y_tunnus}</span>}
              {saaMuokata ? (
                <select value={r.rooli} disabled={kesken}
                        onChange={(e) => void vaihda(r.id, e.target.value)}>
                  {roolit.filter((x) => x.aktiivinen || x.tunnus === r.rooli).map((x) => (
                    <option key={x.tunnus} value={x.tunnus}>{x.nimi}</option>
                  ))}
                </select>
              ) : (
                <span className="merkki">{roolinNimi(r.rooli)}</span>
              )}
              {saaMuokata && (
                <button className="nappi nappi--kevyt" disabled={kesken}
                        onClick={() => void irrota(r.id, r.nimi)}>
                  {tr("koh.irrota")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {saaMuokata && (
        <div className="lomake lomake--tiivis">
          <label>{tr("koh.asiakas_rekisterista")}
            <select value={valittu} disabled={kesken}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__uusi") { setUusiAuki(true); setValittu(""); }
                      else { setUusiAuki(false); setValittu(v === "" ? "" : Number(v)); }
                    }}>
              <option value="">{tr("koh.valitse_viiva")}</option>
              {(rekisteri ?? []).map((a) => (
                <option key={a.id} value={a.id}>{a.nimi}</option>
              ))}
              <option value="__uusi">{tr("koh.uusi_asiakas")}</option>
            </select>
          </label>
          <label>{tr("koh.rooli")}
            <select value={rooli} disabled={kesken}
                    onChange={(e) => setRooli(e.target.value)}>
              {roolit.filter((x) => x.aktiivinen).map((x) => (
                <option key={x.tunnus} value={x.tunnus}>{x.nimi}</option>
              ))}
            </select>
          </label>
          {uusiAuki && (
            <label style={{ flex: "1 1 220px" }}>{tr("koh.uuden_asiakkaan_nimi")}
              <input value={uusiNimi} onChange={(e) => setUusiNimi(e.target.value)}
                     placeholder={tr("koh.asiakas_placeholder")} />
            </label>
          )}
          <button className="nappi nappi--paa" data-liita-kohteeseen=""
                  disabled={kesken || (uusiAuki ? !uusiNimi.trim() : valittu === "")}
                  onClick={() => void liita()}>
            {uusiAuki ? tr("koh.luo_ja_liita") : tr("koh.liita_kohteeseen")}
          </button>
          {/* 81: harmaa nappi sanoo syynsä — rooli on jo oletuksena tilaaja. */}
          {!kesken && (uusiAuki ? !uusiNimi.trim() : valittu === "") && (
            <span className="hint" data-liita-syy="">{uusiAuki ? tr("koh.kirjoita_nimi") : tr("koh.valitse_asiakas")}</span>
          )}
        </div>
      )}

      <p className="ikkuna__vihje">
        {tr("koh.rooli_vihje")}
      </p>
    </>
  );

  async function liita() {
    setKesken(true); setVirhe(null);
    try {
      let asiakas_id = valittu === "" ? 0 : valittu;
      if (uusiAuki) {
        // «Uusi asiakas» samasta valinnasta (Toni 21.9.): jos rekisteristä
        // pitäisi ensin poistua, kytkentä jää tekemättä juuri silloin kun
        // se on tarpeen — eli kun asiakasta ei vielä ole.
        const luotu = await api.luoAsiakas({ nimi: uusiNimi.trim() });
        asiakas_id = luotu.id;
      }
      await api.liitaAsiakasKohteeseen(kohde.id, { asiakas_id: Number(asiakas_id), rooli });
      setValittu(""); setUusiNimi(""); setUusiAuki(false);
      setRivit(null); await hae(); await paivita();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.liittaminen"));
    } finally { setKesken(false); }
  }

  async function vaihda(id: number, uusi: AsiakasRooli) {
    setKesken(true); setVirhe(null);
    try {
      await api.vaihdaKohteenAsiakasRooli(kohde.id, id, uusi);
      setRivit(null); await hae(); await paivita();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.rooli"));
    } finally { setKesken(false); }
  }

  /* Irrotus poistaa LIITOKSEN, ei asiakasta eikä kohteen tekstiä. Sanotaan
     ääneen, koska «Irrota» voisi lukea myös asiakkaan poistona. */
  async function irrota(id: number, nimi: string) {
    if (!window.confirm(
      tr("koh.vahv.irrota", { nimi }) + "\n\n" +
      tr("koh.vahv.irrota_selite")
    )) return;
    setKesken(true); setVirhe(null);
    try {
      await api.irrotaAsiakasKohteesta(kohde.id, id);
      setRivit(null); await hae(); await paivita();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.irrotus"));
    } finally { setKesken(false); }
  }
}

/* ── Työmaaperehdytys ───────────────────────────────────────
   Lista lähtee MIEHITYKSESTÄ eikä perehdytyksistä.

   Perehdytettyjen luettelo vastaisi väärään kysymykseen: työnjohto
   kysyy kuka on tulossa työmaalle ilman perehdytystä, ei kuka on
   perehdytetty. Sama vika kuin tyhjä kenttä joka luetaan todisteeksi
   — lista olisi olemassa ja näyttäisi oikealta.

   Perehdyttäjä esitäytetään tyhjänä eikä kirjautuneesta: työmaa-
   perehdytyksen pitää yleensä pääurakoitsijan työnjohtaja, joka ei ole
   tämän järjestelmän käyttäjä. Jos kenttä jätetään tyhjäksi, kirjaaja
   merkitään perehdyttäjäksi. */
function Perehdytykset({ kohde, saaMuokata }: { kohde: Kohde; saaMuokata: boolean }) {
  const [rivit, setRivit] = useState<Perehdytystilanne[] | null>(null);
  /* 147: avain on «henkilo:vaatimus», ei henkilö — samalla henkilöllä on
     nyt monta riviä, ja pelkkä henkilö avaisi ne kaikki kerralla. */
  const [avoinna, setAvoinna] = useState<string | null>(null);
  const [vaatimukset, setVaatimukset] = useState<PerehdytysVaatimus[] | null>(null);
  const [uusiLaji, setUusiLaji] = useState("");
  const [uusiAlue, setUusiAlue] = useState("");
  const [pitaja, setPitaja] = useState("");
  const [sisalto, setSisalto] = useState("");
  const [asti, setAsti] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);

  const hae = useCallback(async () => {
    try {
      const [t, v] = await Promise.all([
        api.perehdytystilanne(kohde.id),
        api.perehdytysvaatimukset(kohde.id),
      ]);
      setRivit(t); setVaatimukset(v); setVirhe(null);
    }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.perehdytys")); }
  }, [kohde.id]);

  useEffect(() => { if (rivit === null) void hae(); }, [rivit, hae]);

  async function kirjaa(henkilo_id: number, vaatimus_id: number | null) {
    try {
      await api.kirjaaPerehdytys(kohde.id, {
        henkilo_id, vaatimus_id,
        perehdyttaja_nimi: pitaja.trim() || null,
        sisalto: sisalto.trim() || null,
        voimassa_asti: asti || null,
      });
      setAvoinna(null); setPitaja(""); setSisalto(""); setAsti("");
      setRivit(null); void hae();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.kirjaus"));
    }
  }

  const puuttuu = (rivit ?? []).filter((r) => !r.perehdytetty);

  return (
    <>
      <h3>
        {tr("koh.tyomaaperehdytys")}
        {puuttuu.length > 0 && (
          <span className="merkki merkki--vika" style={{ marginLeft: 10 }}>
            {tr("koh.n_puuttuu", { n: puuttuu.length })}
          </span>
        )}
      </h3>

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {rivit === null && !virhe && <p className="dim">{tr("ltar.ladataan")}</p>}

      {rivit?.length === 0 && (
        <p className="dim">
          {tr("koh.ei_miehitysta")}
        </p>
      )}

      {rivit && rivit.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th className="th--vasen">{tr("tk.henkilo")}</th>
              <th>{tr("koh.paivia")}</th>
              <th className="th--vasen">{tr("koh.ensimmainen")}</th>
              <th className="th--vasen">{tr("koh.vaatimus")}</th>
              <th className="th--vasen">{tr("koh.perehdytys")}</th>
              <th className="th--vasen">{tr("koh.perehdyttaja")}</th>
              {saaMuokata && <th />}
            </tr>
          </thead>
          <tbody>
            {rivit.map((r) => (
              <tr key={`${r.henkilo_id}:${r.vaatimus_id ?? "oletus"}`}
                  className={r.perehdytetty ? undefined : "rivi--vika"}>
                <td>{r.nimi}{r.lyhenne && <span className="dim mono"> {r.lyhenne}</span>}</td>
                <td className="mono">{r.allokoituja_pv}</td>
                <td>{naytaPvm(r.ensimmainen_pv)}</td>
                <td>
                  {r.laji}
                  {r.alue && <span className="dim"> · {r.alue}</span>}
                  {/* Oletettu vaatimus ei ole tilaajan vaatimus vaan se sama
                      lause jota havainnointi on aina nostanut. Se sanotaan
                      ääneen, koska muuten se luetaan kirjattuna. */}
                  {r.oletettu && <span className="dim" title={tr("koh.oletus_title")}> · {tr("koh.oletus")}</span>}
                </td>
                <td>
                  {r.perehdytetty
                    ? <>{naytaPvm(r.perehdytys_pvm)}
                        {r.voimassa_asti && <span className="dim"> · {tr("koh.voimassa", { pvm: naytaPvm(r.voimassa_asti) ?? "" })}</span>}</>
                    : r.perehdytys_pvm
                      /* Vanhentunut ja tyhjä ovat molemmat "ei", mutta eri
                         syystä — ja se ero ratkaisee mitä pitää tehdä. */
                      ? <span className="merkki merkki--vika">
                          {tr("koh.vanhentunut", { pvm: naytaPvm(r.voimassa_asti) ?? "" })}
                        </span>
                      : <span className="merkki merkki--vika">{tr("koh.puuttuu")}</span>}
                </td>
                <td className="dim">{r.perehdyttaja ?? "—"}</td>
                {saaMuokata && (
                  <td className="td--oikea">
                    <button className="nappi nappi--kevyt"
                            onClick={() => setAvoinna(avoinna === avain(r) ? null : avain(r))}>
                      {avoinna === avain(r) ? tr("isom.peru") : tr("pu.kirjaa")}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {saaMuokata && avoinna !== null && (
        <div className="lomake lomake--tiivis">
          <label>{tr("koh.perehdyttaja")}
            <input value={pitaja} onChange={(e) => setPitaja(e.target.value)}
                   placeholder={tr("koh.tyhja_sina")} />
          </label>
          <label>{tr("koh.voimassa_asti")}
            <input type="date" value={asti} onChange={(e) => setAsti(e.target.value)} />
          </label>
          <label style={{ flex: "1 1 260px" }}>{tr("koh.mita_kaytiin")}
            <input value={sisalto} onChange={(e) => setSisalto(e.target.value)}
                   placeholder={tr("koh.mita_kaytiin_placeholder")} />
          </label>
          <button className="nappi nappi--paa"
                  onClick={() => { const [h, v] = avoinna.split(":");
                                   void kirjaa(Number(h), v === "oletus" ? null : Number(v)); }}>
            {tr("koh.kirjaa_perehdytys")}
          </button>
        </div>
      )}

      {/* ── Mitä tämä työmaa vaatii ──────────────────────────
          Vaatimus on kohteen ominaisuus, suoritus henkilön tapahtuma.
          Pekilotehdas on Sokeritehtaan alueella, joten vaatimuksia on
          kaksi: alueen oma ja työmaan oma. Alue on oma kenttänsä eikä
          osa lajin nimeä — nimeen upotettuna sillä ei voi suodattaa. */}
      <h4>{tr("koh.mita_tyomaa_vaatii")}</h4>
      {vaatimukset?.some((v) => v.oletettu) && (
        <p className="dim">
          {tr("koh.oletettu_selite")}
        </p>
      )}
      {vaatimukset && vaatimukset.length > 0 && !vaatimukset.some((v) => v.oletettu) && (
        <ul className="tarvevaatimukset">
          {vaatimukset.map((v) => (
            <li key={v.vaatimus_id ?? "oletus"}>
              {v.laji}
              {v.alue && <span className="dim"> · {v.alue}</span>}
              {!v.pakollinen && <span className="dim"> · {tr("koh.vapaaehtoinen")}</span>}
              {saaMuokata && v.vaatimus_id !== null && (
                <button className="nappi nappi--kevyt"
                        onClick={() => void poistaVaatimus(v.vaatimus_id!, v.laji)}>
                  {tr("mit.poista_vaatimus")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {saaMuokata && (
        <div className="lomake lomake--tiivis">
          <label>{tr("koh.perehdytyksen_laji")}
            <input value={uusiLaji} onChange={(e) => setUusiLaji(e.target.value)}
                   placeholder={tr("koh.laji_placeholder")} />
          </label>
          <label>{tr("koh.alue")}
            <input value={uusiAlue} onChange={(e) => setUusiAlue(e.target.value)}
                   placeholder={tr("koh.alue_placeholder")} />
          </label>
          <button className="nappi" disabled={!uusiLaji.trim()}
                  onClick={() => void lisaaVaatimus()}>
            {tr("koh.lisaa_vaatimus")}
          </button>
        </div>
      )}

      <p className="ikkuna__vihje">
        {tr("koh.perehdytys_vihje")}
      </p>
    </>
  );

  async function lisaaVaatimus() {
    try {
      await api.lisaaPerehdytysvaatimus(kohde.id, {
        laji: uusiLaji.trim(),
        ...(uusiAlue.trim() ? { alue: uusiAlue.trim() } : {}),
      });
      setUusiLaji(""); setUusiAlue(""); setRivit(null); await hae();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.vaatimus_lisays"));
    }
  }

  /* Vaatimuksen poisto ei poista kirjattuja perehdytyksiä: `vaatimus_id`
     on ON DELETE SET NULL, joten suoritus jää «ei asetettu» -tilaan.
     Käyttäjän kirjaamaa riviä ei poisteta toisen rivin mukana. */
  async function poistaVaatimus(id: number, laji: string) {
    if (!window.confirm(
      tr("koh.vahv.poista_perehdytysvaatimus", { laji }) + "\n\n" +
      tr("koh.vahv.perehdytykset_sailyvat")
    )) return;
    try {
      await api.poistaPerehdytysvaatimus(kohde.id, id);
      setRivit(null); await hae();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.vaatimus_poisto"));
    }
  }
}

/** Rivin avain: sama henkilö esiintyy kerran per vaatimus. */
const avain = (r: Perehdytystilanne) => `${r.henkilo_id}:${r.vaatimus_id ?? "oletus"}`;

/* ── Päätös 80 (Toni 5.10.2026): tunnuksen muokkaus ja «Uusi kohde jatkona» ────────────────
   Tunnuksen saa muuttaa, jos kohteella ei ole kirjauksia, tai kirjoitusvirheen korjauksena; peruste pakollinen.
   Reitti päättää (409 kohteella_kirjauksia lukuineen) — selain näyttää syyn ja tarjoaa jatkon. */
function KohteenTunnus({ kohde, paivita }: { kohde: Kohde; paivita: () => Promise<void> }) {
  const [auki, setAuki] = useState(false);
  const [tunnus, setTunnus] = useState(kohde.tunnus);
  const [peruste, setPeruste] = useState("");
  const [korjaus, setKorjaus] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [esto, setEsto] = useState<string | null>(null);
  const [jatkoAuki, setJatkoAuki] = useState(false);
  const [jatkoTunnus, setJatkoTunnus] = useState("");
  const [jatkoNimi, setJatkoNimi] = useState(kohde.nimi);
  const [kesken, setKesken] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  async function tallenna() {
    setKesken(true); setVirhe(null); setEsto(null);
    try {
      const r = await api.muokkaaKohteenTunnusta(kohde.id, { tunnus: tunnus.trim(), peruste: peruste.trim(), korjaus });
      setIlmoitus(tr("koh.ilm.tunnus_vaihdettu", { vanha: r.entinen_tunnus ?? "", uusi: r.tunnus }));
      setAuki(false); await paivita();
    } catch (e) {
      if (e instanceof ApiError && e.virhe === "kohteella_kirjauksia") { setEsto(virheTeksti(e.virhe, e.message)); setJatkoAuki(true); }
      else setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.muutos"));
    } finally { setKesken(false); }
  }
  async function luoJatko() {
    setKesken(true); setVirhe(null);
    try {
      const u = await api.luoKohde({ tunnus: jatkoTunnus.trim(), nimi: jatkoNimi.trim(), laji: kohde.laji, alkaa: new Date().toISOString().slice(0, 10), jatkaa_kohde_id: kohde.id });
      setIlmoitus(tr("koh.ilm.jatko_luotu", { uusi: u.tunnus, vanha: kohde.tunnus }));
      setJatkoAuki(false); setAuki(false); await paivita();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("koh.virhe.luonti")); }
    finally { setKesken(false); }
  }
  return (
    <div className="lohko" data-kohteen-tunnus={kohde.id}>
      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
      {!auki && (
        <button type="button" className="nappi nappi--kevyt" data-tunnus-muokkaa="" onClick={() => { setAuki(true); setTunnus(kohde.tunnus); }}>
          {tr("koh.muokkaa_tunnusta")}{kohde.entinen_tunnus ? ` (${tr("koh.entinen", { tunnus: kohde.entinen_tunnus })})` : ""}
        </button>
      )}
      {auki && (
        <div className="lomake lomake--tiivis">
          <label>{tr("koh.uusi_tunnus")}<input value={tunnus} onChange={(e) => setTunnus(e.target.value)} maxLength={40} data-tunnus-kentta="" /></label>
          <label style={{ flex: "1 1 260px" }}>{tr("qr.peruste")}<input value={peruste} onChange={(e) => setPeruste(e.target.value)} placeholder={tr("koh.tunnus_peruste_placeholder")} data-tunnus-peruste="" /></label>
          <label className="valinta"><input type="checkbox" checked={korjaus} onChange={(e) => setKorjaus(e.target.checked)} /> {tr("koh.kirjoitusvirhe")}</label>
          <button type="button" className="nappi nappi--paa" disabled={kesken || tunnus.trim() === kohde.tunnus || peruste.trim().length < 3} onClick={() => void tallenna()} data-tunnus-tallenna="">{tr("koh.vaihda_tunnus")}</button>
          <button type="button" className="nappi nappi--kevyt" onClick={() => { setAuki(false); setEsto(null); setJatkoAuki(false); }}>{tr("isom.peru")}</button>
          {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
          {esto && (
            <div className="huom" role="alert" data-tunnus-esto="">
              <p>{esto}</p>
              {jatkoAuki && (
                <div className="lomake lomake--tiivis" data-jatko-lomake="">
                  <strong>{tr("koh.uusi_jatkona")}</strong>
                  <label>{tr("koh.uusi_tunnus")}<input value={jatkoTunnus} onChange={(e) => setJatkoTunnus(e.target.value)} maxLength={40} /></label>
                  <label>{tr("koh.nimi")}<input value={jatkoNimi} onChange={(e) => setJatkoNimi(e.target.value)} maxLength={200} /></label>
                  <button type="button" className="nappi nappi--paa" disabled={kesken || !jatkoTunnus.trim() || !jatkoNimi.trim()} onClick={() => void luoJatko()} data-jatko-luo="">{tr("koh.luo_jatkona")}</button>
                  <span className="hint">{tr("koh.jatko_hint")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
